<template>

  <!-- ======================================== -->
  <!-- MONITOR MODE (fullscreen dashboard) -->
  <!-- ======================================== -->
  <div v-if="monitorMode" class="fixed inset-0 z-[100] flex flex-col bg-gradient-to-br from-slate-50 via-slate-100 to-slate-200 dark:from-slate-900 dark:via-slate-900 dark:to-slate-800 overflow-hidden" @click="closeMonitorCallMenu">

    <!-- Floating Top Bar -->
    <div class="flex items-center justify-between px-4 py-2 bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm border-b border-slate-200/50 dark:border-slate-700/50 flex-shrink-0">
      <div class="flex items-center gap-3">
        <!-- Compact View Selector -->
        <div class="relative" ref="monitorViewSelectorRef">
          <button
            @click.stop="toggleMonitorViewSelector"
            class="flex items-center gap-1.5 px-2.5 py-1.5 bg-white/80 dark:bg-slate-700/80 border border-slate-200/60 dark:border-slate-600/60 rounded-lg hover:bg-white dark:hover:bg-slate-700 transition-colors text-left"
          >
            <LayoutGrid class="w-3.5 h-3.5 text-slate-400" />
            <span class="text-xs font-semibold text-slate-700 dark:text-slate-200 truncate max-w-[150px]">{{ activeView?.view_name || 'Dashboard' }}</span>
            <ChevronDown class="w-3 h-3 text-slate-400" />
          </button>
        </div>

        <!-- Presence summary mini -->
        <div class="hidden lg:flex items-center gap-2 text-[11px] font-medium">
          <span class="flex items-center gap-1 text-emerald-600"><span class="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>{{ presenceStats.available }}</span>
          <span class="flex items-center gap-1 text-red-500"><span class="w-1.5 h-1.5 rounded-full bg-red-500"></span>{{ presenceStats.busy }}</span>
          <span class="flex items-center gap-1 text-amber-500"><span class="w-1.5 h-1.5 rounded-full bg-amber-500"></span>{{ presenceStats.away }}</span>
        </div>
      </div>

      <div class="flex items-center gap-1">
        <!-- DND / Status -->
        <div v-if="currentUserId" class="relative" ref="monitorDndRef">
          <button
            @click.stop="toggleMonitorDndMenu"
            :disabled="isSettingPresence"
            class="relative p-1.5 rounded-lg transition-all duration-200"
            :class="isDnd
              ? 'text-red-500 bg-red-50 dark:bg-red-900/30 ring-1 ring-red-200 dark:ring-red-800/50'
              : 'text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700'"
            :title="isDnd ? activeDndLabel : 'Status setzen'"
          >
            <Loader2 v-if="isSettingPresence" class="w-4 h-4 animate-spin" />
            <Coffee v-else-if="activeDndPreset === 'coffee'" class="w-4 h-4" />
            <UtensilsCrossed v-else-if="activeDndPreset === 'lunch'" class="w-4 h-4" />
            <BellOff v-else-if="isDnd" class="w-4 h-4" />
            <Bell v-else class="w-4 h-4" />
            <span v-if="isDnd" class="absolute -top-0.5 -right-0.5 w-2 h-2 bg-red-500 rounded-full ring-2 ring-white dark:ring-slate-800"></span>
          </button>
        </div>

        <!-- Call Mode Toggle -->
        <button
          @click.stop="setCallMode(callMode === 'teams' ? 'native' : 'teams')"
          class="p-1.5 rounded-lg transition-all duration-200"
          :class="callMode === 'teams'
            ? 'text-purple-600 bg-purple-50 dark:bg-purple-900/30 dark:text-purple-400'
            : 'text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700'"
          :title="callMode === 'teams' ? 'Telefonate über Teams (klicken für natives Telefon)' : 'Natives Telefon (klicken für Teams)'"
        >
          <PhoneCall class="w-4 h-4" />
        </button>

        <div class="w-px h-5 bg-slate-200 dark:bg-slate-700 mx-1"></div>

        <!-- Exit Monitor Mode -->
        <button
          @click="emit('toggle-monitor-mode')"
          class="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-semibold text-slate-500 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors"
          title="Monitor-Modus beenden (ESC)"
        >
          <Minimize2 class="w-3.5 h-3.5" />
          <span class="hidden sm:inline">Beenden</span>
        </button>
      </div>
    </div>

    <!-- View Selector Dropdown (Teleported for z-index safety) -->
    <Teleport to="body">
      <Transition
        enter-active-class="transition-all duration-150 ease-out"
        enter-from-class="opacity-0 scale-95 -translate-y-1"
        enter-to-class="opacity-100 scale-100 translate-y-0"
        leave-active-class="transition-all duration-100 ease-in"
        leave-from-class="opacity-100 scale-100"
        leave-to-class="opacity-0 scale-95"
      >
        <div v-if="monitorViewSelectorOpen" class="fixed z-[9999]" :style="monitorViewSelectorPos" @click.stop>
          <div class="w-64 bg-white dark:bg-slate-800 border border-slate-200/80 dark:border-slate-600/80 rounded-xl shadow-2xl overflow-hidden">
            <div class="px-3 pt-2 pb-1">
              <p class="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Ansichten</p>
            </div>
            <div class="px-1.5 max-h-60 overflow-y-auto">
              <button
                v-for="v in [...ownViews, ...sharedViews]"
                :key="v.id"
                @click="switchView(v.id); monitorViewSelectorOpen = false"
                class="w-full flex items-center gap-2 px-2.5 py-1.5 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors text-left"
                :class="{ 'bg-blue-50/60 dark:bg-blue-900/20': v.id === activeViewId }"
              >
                <span class="text-xs font-medium truncate flex-1" :class="v.id === activeViewId ? 'text-blue-700 dark:text-blue-300' : 'text-slate-700 dark:text-slate-300'">{{ v.view_name }}</span>
                <span class="text-[10px] text-slate-400">{{ v.group_count || 0 }}G</span>
              </button>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>

    <!-- DND Dropdown (Teleported for z-index safety) -->
    <Teleport to="body">
      <Transition
        enter-active-class="transition duration-150 ease-out"
        enter-from-class="opacity-0 scale-95 -translate-y-1"
        enter-to-class="opacity-100 scale-100 translate-y-0"
        leave-active-class="transition duration-100 ease-in"
        leave-from-class="opacity-100 scale-100"
        leave-to-class="opacity-0 scale-95"
      >
        <div v-if="monitorDndOpen && !isSettingPresence" class="fixed z-[9999]" :style="monitorDndPos" @click.stop>
          <div class="w-52 bg-white/95 dark:bg-slate-800/95 backdrop-blur-xl rounded-xl shadow-xl border border-slate-200/80 dark:border-slate-700/80 overflow-hidden">
            <button v-if="isDnd" @click="clearDnd(); monitorDndOpen = false" class="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-sm text-emerald-600 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 transition-colors">
              <Bell class="w-4 h-4" /><span class="font-medium">Verfügbar</span><span class="ml-auto text-[11px] text-slate-400">Zurücksetzen</span>
            </button>
            <div v-if="isDnd" class="border-t border-slate-100 dark:border-slate-700/60"></div>
            <button @click="setDndPreset('coffee', 5); monitorDndOpen = false" class="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-sm transition-colors" :class="activeDndPreset === 'coffee' ? 'text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/25' : 'text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700/50'">
              <Coffee class="w-4 h-4 text-amber-500" /><span class="font-medium">Kurze Pause</span><span class="ml-auto text-[11px] text-slate-400">5 Min</span>
            </button>
            <button @click="setDndPreset('lunch', 30); monitorDndOpen = false" class="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-sm transition-colors" :class="activeDndPreset === 'lunch' ? 'text-orange-700 dark:text-orange-300 bg-orange-50 dark:bg-orange-900/25' : 'text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700/50'">
              <UtensilsCrossed class="w-4 h-4 text-orange-500" /><span class="font-medium">Mittagspause</span><span class="ml-auto text-[11px] text-slate-400">30 Min</span>
            </button>
            <button @click="setDndPreset('dnd', 480); monitorDndOpen = false" class="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-sm transition-colors" :class="activeDndPreset === 'dnd' ? 'text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/25' : 'text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700/50'">
              <BellOff class="w-4 h-4 text-red-500" /><span class="font-medium">Nicht stören</span><span class="ml-auto text-[11px] text-slate-400">8 Std</span>
            </button>
          </div>
        </div>
      </Transition>
    </Teleport>

    <!-- Main Content: 2/3 Groups + 1/3 Sidebar -->
    <div class="flex-1 flex overflow-hidden">

      <!-- LEFT: Groups (2/3) -->
      <div class="flex-1 overflow-y-auto p-3 lg:p-4 space-y-3 scrollbar-thin scrollbar-thumb-slate-200 dark:scrollbar-thumb-slate-700 scrollbar-track-transparent" style="flex: 2;">

        <!-- Loading State -->
        <div v-if="loading" class="flex items-center justify-center py-16">
          <Loader2 class="w-8 h-8 text-blue-500 animate-spin" />
        </div>

        <!-- Empty State -->
        <div v-else-if="groups.length === 0" class="flex flex-col items-center justify-center py-16 text-slate-400">
          <LayoutGrid class="w-12 h-12 mb-3 opacity-40" />
          <p class="text-sm font-medium">Keine Gruppen vorhanden</p>
        </div>

        <!-- Groups -->
        <div v-else v-for="(group, groupIndex) in groups" :key="group.id" class="bg-white/90 dark:bg-slate-800/90 border border-slate-200/50 dark:border-slate-700/50 rounded-xl">
          <!-- Group Header -->
          <div class="flex items-center gap-2 px-3 py-2 cursor-pointer" @click="toggleCollapse(group)">
            <div class="w-2 h-2 rounded-full flex-shrink-0" :class="group.color || groupColors[groupIndex % groupColors.length]"></div>
            <h3 class="text-xs font-bold text-slate-700 dark:text-slate-200 truncate flex-1">{{ group.group_name }}</h3>
            <span class="text-[10px] text-slate-400 font-semibold">{{ group.contacts?.length || 0 }}</span>
            <!-- Presence dots summary -->
            <div class="hidden sm:flex items-center gap-0.5">
              <div v-for="status in getGroupPresenceSummary(group)" :key="status.class" class="w-1.5 h-1.5 rounded-full" :class="status.class" :title="`${status.count} ${status.label}`"></div>
            </div>
            <ChevronDown v-if="group.is_collapsed" class="w-3.5 h-3.5 text-slate-400" />
            <ChevronUp v-else class="w-3.5 h-3.5 text-slate-400" />
          </div>

          <!-- Ultra-compact Contact Grid -->
          <div v-show="!group.is_collapsed" class="px-2 pb-2">
            <div class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 2xl:grid-cols-8 gap-1">
              <div
                v-for="contact in group.contacts"
                :key="contact.contact_email"
                @click.stop="monitorContactClick($event, contact)"
                class="relative flex items-center gap-2 p-1.5 rounded-lg cursor-pointer transition-all duration-150 group/mc"
                :class="[
                  isInCall(contact.entra_id) ? 'bg-red-50/60 dark:bg-red-900/15 ring-1 ring-red-200/60 dark:ring-red-800/30' :
                  isInMeeting(contact.entra_id) ? 'bg-purple-50/40 dark:bg-purple-900/10 ring-1 ring-purple-200/40 dark:ring-purple-800/20' :
                  'hover:bg-slate-50 dark:hover:bg-slate-700/30'
                ]"
                :title="contact.display_name + (getPresenceLabel(contact.entra_id) ? ' — ' + getPresenceLabel(contact.entra_id) : '')"
              >
                <!-- Mini Avatar + Presence -->
                <div class="relative flex-shrink-0">
                  <img
                    v-if="contactPhotos[contact.contact_email]"
                    :src="contactPhotos[contact.contact_email]"
                    :alt="contact.display_name"
                    class="w-7 h-7 rounded-lg object-cover"
                  >
                  <div
                    v-else
                    class="w-7 h-7 rounded-lg flex items-center justify-center"
                    :class="avatarGradients[getInitials(contact.display_name).charCodeAt(0) % avatarGradients.length]"
                  >
                    <span class="text-[10px] font-bold text-white">{{ getInitials(contact.display_name) }}</span>
                  </div>
                  <div
                    v-if="contact.entra_id"
                    class="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-[2px] border-white dark:border-slate-800"
                    :class="[getPresenceClass(contact.entra_id), { 'animate-pulse': isInCall(contact.entra_id) }]"
                  ></div>
                </div>

                <!-- Name -->
                <div class="flex-1 min-w-0">
                  <span class="text-[11px] font-semibold text-slate-700 dark:text-slate-200 truncate block leading-tight">
                    {{ contact.display_name?.split(' ')[0] || contact.contact_email }}
                  </span>
                  <span class="text-[10px] text-slate-400 dark:text-slate-500 truncate block leading-tight">
                    <template v-if="isInCall(contact.entra_id)">Im Anruf</template>
                    <template v-else-if="isInMeeting(contact.entra_id)">Im Meeting</template>
                    <template v-else>{{ contact.display_name?.split(' ').slice(1).join(' ') || '' }}</template>
                  </span>
                </div>

                <!-- Heart indicator (if has call preference) -->
                <Heart
                  v-if="getCallPreference(contact.contact_email)"
                  class="w-2.5 h-2.5 text-red-400 fill-red-400 flex-shrink-0 opacity-60"
                />

                <!-- Hover call indicator -->
                <PhoneCall class="w-3 h-3 text-emerald-500 flex-shrink-0 opacity-0 group-hover/mc:opacity-100 transition-opacity absolute right-1.5 top-1.5" />
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- RIGHT: Sidebar (1/3) -->
      <div class="border-l border-slate-200/50 dark:border-slate-700/50 bg-white/60 dark:bg-slate-800/60 flex flex-col overflow-hidden" style="flex: 1; max-width: 380px; min-width: 280px;">

        <!-- Quick Dial Section -->
        <div class="p-3 border-b border-slate-200/50 dark:border-slate-700/50 flex-shrink-0">
          <div class="relative">
            <input
              ref="quickDialInputRef"
              v-model="quickDialInput"
              @input="onQuickDialInput"
              @keydown.enter="executeQuickDial"
              placeholder="Name oder Nummer..."
              class="w-full pl-8 pr-8 py-2 text-sm bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-600 rounded-xl focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 text-slate-800 dark:text-slate-100 outline-none placeholder-slate-400"
            />
            <Search class="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
            <button
              v-if="quickDialInput"
              @click="clearQuickDial"
              class="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
            >
              <X class="w-3.5 h-3.5" />
            </button>
          </div>

          <!-- Quick Dial Search Results -->
          <div v-if="showQuickDialResults && quickDialResults.length > 0" class="mt-1.5 max-h-48 overflow-y-auto rounded-lg border border-slate-200/80 dark:border-slate-600/60 bg-white dark:bg-slate-800 shadow-lg">
            <button
              v-for="c in quickDialResults"
              :key="c.email"
              @click.stop="monitorQuickCall(c)"
              class="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors border-b border-slate-100 dark:border-slate-700/50 last:border-b-0"
            >
              <div class="relative flex-shrink-0">
                <div class="w-6 h-6 rounded-md flex items-center justify-center" :class="avatarGradients[getInitials(c.display_name).charCodeAt(0) % avatarGradients.length]">
                  <span class="text-[9px] font-bold text-white">{{ getInitials(c.display_name) }}</span>
                </div>
                <div v-if="c.entra_id" class="absolute -bottom-0.5 -right-0.5 w-2 h-2 rounded-full border-[1.5px] border-white dark:border-slate-800" :class="getPresenceClass(c.entra_id)"></div>
              </div>
              <div class="flex-1 min-w-0">
                <span class="text-xs font-semibold text-slate-700 dark:text-slate-200 truncate block">{{ c.display_name }}</span>
                <span class="text-[10px] text-slate-400 truncate block">{{ c.company_name || c.job_title || '' }}</span>
              </div>
              <PhoneCall class="w-3 h-3 text-emerald-500 flex-shrink-0" />
            </button>
          </div>

          <!-- Dialpad -->
          <div class="mt-2 grid grid-cols-3 gap-1">
            <template v-for="row in dialpadKeys" :key="row.join('')">
              <button
                v-for="key in row"
                :key="key"
                @click="appendDialpad(key)"
                class="py-2 text-sm font-semibold text-slate-700 dark:text-slate-200 bg-slate-50 dark:bg-slate-700/50 hover:bg-slate-100 dark:hover:bg-slate-600/50 rounded-lg transition-colors active:scale-95"
              >
                {{ key }}
              </button>
            </template>
          </div>

          <!-- Call button -->
          <button
            v-if="quickDialInput.trim()"
            @click="executeQuickDial"
            class="w-full mt-2 flex items-center justify-center gap-2 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold rounded-xl transition-colors shadow-sm"
          >
            <PhoneCall class="w-3.5 h-3.5" />
            Anrufen
          </button>
        </div>

        <!-- Recent Calls -->
        <div class="flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-slate-200 dark:scrollbar-thumb-slate-700 scrollbar-track-transparent">
          <div class="px-3 py-2 flex items-center justify-between sticky top-0 bg-white/90 dark:bg-slate-800/90 backdrop-blur-sm z-10">
            <h3 class="text-xs font-bold text-slate-600 dark:text-slate-300">Letzte Anrufe</h3>
            <span class="text-[10px] text-slate-400 font-semibold">{{ monitorCallRecords.length }}</span>
          </div>
          <div v-if="dashboardCallsLoading" class="flex items-center justify-center py-8">
            <Loader2 class="w-5 h-5 text-blue-500 animate-spin" />
          </div>
          <div v-else-if="monitorCallRecords.length === 0" class="py-8 text-center">
            <Phone class="w-5 h-5 mx-auto mb-2 text-slate-300 dark:text-slate-500" />
            <p class="text-xs text-slate-400">Keine Anrufe</p>
          </div>
          <div v-else class="px-2 pb-2 space-y-0.5">
            <div
              v-for="call in monitorCallRecords"
              :key="call.id"
              class="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700/30 transition-colors cursor-pointer"
              @click.stop="monitorCallbackFromRecord(call)"
              :title="getMonitorCallTitle(call)"
            >
              <!-- Direction Icon -->
              <div class="w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0" :class="getMonitorCallIconClass(call)">
                <PhoneCall v-if="!call.is_missed" class="w-3 h-3 text-white" />
                <Phone v-else class="w-3 h-3 text-white" />
              </div>
              <!-- Name + Time -->
              <div class="flex-1 min-w-0">
                <span class="text-[11px] font-semibold text-slate-700 dark:text-slate-200 truncate block">{{ getMonitorCallName(call) }}</span>
                <span class="text-[10px] text-slate-400 block">{{ getMonitorCallTime(call) }}</span>
              </div>
              <!-- Duration -->
              <span class="text-[10px] text-slate-400 flex-shrink-0">{{ formatDuration(call.duration_seconds) }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Monitor Mode Call Menu (Teleported) -->
    <Teleport to="body">
      <Transition
        enter-active-class="transition-all duration-150 ease-out"
        enter-from-class="opacity-0 scale-95"
        enter-to-class="opacity-100 scale-100"
        leave-active-class="transition-all duration-100 ease-in"
        leave-from-class="opacity-100 scale-100"
        leave-to-class="opacity-0 scale-95"
      >
        <div
          v-if="monitorCallMenu && monitorCallMenuContact"
          class="fixed z-[9999]"
          :style="monitorCallMenuPos"
          @click.stop
        >
          <div class="bg-white dark:bg-slate-800 border border-slate-200/80 dark:border-slate-600/80 rounded-xl shadow-2xl shadow-black/20 py-1 min-w-[220px]">
            <div class="px-3 py-1.5 border-b border-slate-100 dark:border-slate-700/60">
              <span class="text-xs font-bold text-slate-700 dark:text-slate-200">{{ monitorCallMenuContact.display_name }}</span>
            </div>
            <!-- Teams Call -->
            <div
              v-if="hasValidEmail(monitorCallMenuContact)"
              @click.stop="monitorCallAndClose(monitorCallMenuContact, 'teams')"
              class="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700/60 transition-colors cursor-pointer"
            >
              <PhoneCall class="w-3.5 h-3.5 text-purple-500" />
              <span class="flex-1 text-left text-xs font-medium">Teams Anruf</span>
              <button @click.stop="monitorSetHeart(monitorCallMenuContact, 'teams')" class="p-0.5 rounded hover:bg-slate-100 dark:hover:bg-slate-600">
                <Heart class="w-3 h-3" :class="getCallPreference(monitorCallMenuContact.contact_email) === 'teams' ? 'text-red-500 fill-red-500' : 'text-slate-300 dark:text-slate-500'" />
              </button>
            </div>
            <!-- Business Phone -->
            <div
              v-if="getBusinessPhone(monitorCallMenuContact)"
              @click.stop="monitorCallAndClose(monitorCallMenuContact, 'business')"
              class="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700/60 transition-colors cursor-pointer"
            >
              <Phone class="w-3.5 h-3.5 text-green-500" />
              <div class="flex-1 text-left">
                <span class="text-xs font-medium block">Telefon</span>
                <span class="text-[10px] text-slate-400">{{ getBusinessPhone(monitorCallMenuContact) }}</span>
              </div>
              <button @click.stop="monitorSetHeart(monitorCallMenuContact, 'business')" class="p-0.5 rounded hover:bg-slate-100 dark:hover:bg-slate-600">
                <Heart class="w-3 h-3" :class="getCallPreference(monitorCallMenuContact.contact_email) === 'business' ? 'text-red-500 fill-red-500' : 'text-slate-300 dark:text-slate-500'" />
              </button>
            </div>
            <!-- Mobile Phone -->
            <div
              v-if="getMobilePhone(monitorCallMenuContact)"
              @click.stop="monitorCallAndClose(monitorCallMenuContact, 'mobile')"
              class="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700/60 transition-colors cursor-pointer"
            >
              <Smartphone class="w-3.5 h-3.5 text-blue-500" />
              <div class="flex-1 text-left">
                <span class="text-xs font-medium block">Mobil</span>
                <span class="text-[10px] text-slate-400">{{ getMobilePhone(monitorCallMenuContact) }}</span>
              </div>
              <button @click.stop="monitorSetHeart(monitorCallMenuContact, 'mobile')" class="p-0.5 rounded hover:bg-slate-100 dark:hover:bg-slate-600">
                <Heart class="w-3 h-3" :class="getCallPreference(monitorCallMenuContact.contact_email) === 'mobile' ? 'text-red-500 fill-red-500' : 'text-slate-300 dark:text-slate-500'" />
              </button>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>
  </div>

  <!-- ======================================== -->
  <!-- NORMAL MODE (existing dashboard) -->
  <!-- ======================================== -->
  <div v-else class="space-y-5">

    <!-- Pending Invitations Banner -->
    <div v-if="pendingInvitations.length > 0" class="space-y-2">
      <div
        v-for="inv in pendingInvitations"
        :key="inv.id || `public-${inv.view_id}`"
        class="flex flex-col sm:flex-row sm:items-center justify-between gap-2 sm:gap-3 p-3 sm:p-3.5 rounded-2xl border"
        :class="inv.is_public_invite
          ? 'bg-gradient-to-r from-emerald-50 via-teal-50 to-cyan-50 dark:from-emerald-900/20 dark:via-teal-900/15 dark:to-cyan-900/20 border-emerald-200/60 dark:border-emerald-800/40'
          : 'bg-gradient-to-r from-blue-50 via-indigo-50 to-violet-50 dark:from-blue-900/20 dark:via-indigo-900/15 dark:to-violet-900/20 border-blue-200/60 dark:border-blue-800/40'"
      >
        <div class="flex items-center gap-2.5 sm:gap-3 min-w-0">
          <div class="w-8 h-8 sm:w-9 sm:h-9 rounded-xl flex items-center justify-center flex-shrink-0"
            :class="inv.is_public_invite ? 'bg-emerald-500/15 dark:bg-emerald-500/25' : 'bg-blue-500/15 dark:bg-blue-500/25'"
          >
            <Globe v-if="inv.is_public_invite" class="w-4 h-4 sm:w-4.5 sm:h-4.5 text-emerald-600 dark:text-emerald-400" />
            <Mail v-else class="w-4 h-4 sm:w-4.5 sm:h-4.5 text-blue-600 dark:text-blue-400" />
          </div>
          <div class="min-w-0">
            <p class="text-xs sm:text-sm font-semibold text-slate-800 dark:text-slate-200">
              <span class="truncate">{{ inv.shared_by_name || inv.owner_name }}</span> hat
              <span :class="inv.is_public_invite ? 'text-emerald-600 dark:text-emerald-400' : 'text-blue-600 dark:text-blue-400'">"{{ inv.view_name }}"</span>
              {{ inv.is_public_invite ? 'öffentlich geteilt' : `als ${inv.role === 'editor' ? 'Editor' : 'Leser'} geteilt` }}
            </p>
          </div>
        </div>
        <div class="flex items-center gap-2 flex-shrink-0 ml-10 sm:ml-0">
          <button
            @click="acceptInvitation(inv)"
            class="px-3 py-1.5 text-xs font-semibold text-white rounded-lg transition-colors shadow-sm"
            :class="inv.is_public_invite ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-blue-600 hover:bg-blue-700'"
          >
            Annehmen
          </button>
          <button
            @click="declineInvitation(inv)"
            class="px-3 py-1.5 text-xs font-semibold text-slate-600 dark:text-slate-300 hover:bg-slate-200/60 dark:hover:bg-slate-700/60 rounded-lg transition-colors"
          >
            {{ inv.is_public_invite ? 'Ausblenden' : 'Ablehnen' }}
          </button>
        </div>
      </div>
    </div>

    <!-- View Selector Bar -->
    <div class="flex items-center justify-between gap-2 sm:gap-3">
      <div class="flex items-center gap-1.5 sm:gap-2 min-w-0 flex-1">
        <!-- View Dropdown -->
        <div class="relative" ref="viewSelectorRef">
          <button
            @click="showViewSelector = !showViewSelector"
            class="flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-3 py-2 bg-white/80 dark:bg-slate-800/80 border border-slate-200/60 dark:border-slate-700/60 rounded-xl hover:bg-white dark:hover:bg-slate-800 transition-colors shadow-sm text-left min-w-0"
          >
            <LayoutGrid class="w-4 h-4 text-slate-500 dark:text-slate-400 flex-shrink-0 hidden sm:block" />
            <span class="text-xs sm:text-sm font-semibold text-slate-800 dark:text-slate-200 truncate max-w-[120px] sm:max-w-[200px]">
              {{ activeView?.view_name || 'Dashboard' }}
            </span>
            <span v-if="activeView?.role === 'reader'" class="px-1.5 py-0.5 text-[9px] font-bold bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 rounded-md flex-shrink-0">NUR LESEN</span>
            <span v-else-if="activeView?.role === 'editor'" class="px-1.5 py-0.5 text-[9px] font-bold bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 rounded-md flex-shrink-0">EDITOR</span>
            <ChevronDown class="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
          </button>

          <!-- Dropdown menu -->
          <Transition
            enter-active-class="transition-all duration-150 ease-out"
            enter-from-class="opacity-0 scale-95 -translate-y-1"
            enter-to-class="opacity-100 scale-100 translate-y-0"
            leave-active-class="transition-all duration-100 ease-in"
            leave-from-class="opacity-100 scale-100"
            leave-to-class="opacity-0 scale-95"
          >
            <div
              v-if="showViewSelector"
              class="absolute left-0 top-full mt-1.5 z-50 w-[calc(100vw-2rem)] sm:w-72 max-w-72 bg-white dark:bg-slate-800 border border-slate-200/80 dark:border-slate-600/80 rounded-xl shadow-2xl shadow-black/15 overflow-hidden"
            >
              <!-- Own views -->
              <div class="px-3 pt-3 pb-1">
                <p class="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider">Eigene Ansichten</p>
              </div>
              <div class="px-1.5">
                <button
                  v-for="v in ownViews"
                  :key="v.id"
                  @click="switchView(v.id)"
                  class="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors text-left"
                  :class="{ 'bg-blue-50/60 dark:bg-blue-900/20': v.id === activeViewId }"
                >
                  <LayoutGrid class="w-3.5 h-3.5 flex-shrink-0" :class="v.id === activeViewId ? 'text-blue-500' : 'text-slate-400'" />
                  <span class="text-sm font-medium truncate flex-1" :class="v.id === activeViewId ? 'text-blue-700 dark:text-blue-300' : 'text-slate-700 dark:text-slate-300'">{{ v.view_name }}</span>
                  <Star v-if="v.is_default" class="w-3 h-3 text-amber-400 flex-shrink-0" :fill="'currentColor'" />
                  <span class="text-[10px] text-slate-400 flex-shrink-0">{{ v.group_count }}G · {{ v.contact_count }}K</span>
                </button>
              </div>

              <!-- Shared views -->
              <template v-if="sharedViews.length > 0">
                <div class="px-3 pt-3 pb-1 border-t border-slate-100 dark:border-slate-700 mt-1">
                  <p class="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider">Geteilte Ansichten</p>
                </div>
                <div class="px-1.5">
                  <button
                    v-for="v in sharedViews"
                    :key="v.id"
                    @click="switchView(v.id)"
                    class="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors text-left"
                    :class="{ 'bg-blue-50/60 dark:bg-blue-900/20': v.id === activeViewId }"
                  >
                    <Share2 class="w-3.5 h-3.5 flex-shrink-0" :class="v.id === activeViewId ? 'text-blue-500' : 'text-slate-400'" />
                    <div class="flex-1 min-w-0">
                      <span class="text-sm font-medium truncate block" :class="v.id === activeViewId ? 'text-blue-700 dark:text-blue-300' : 'text-slate-700 dark:text-slate-300'">{{ v.view_name }}</span>
                      <span class="text-[10px] text-slate-400 truncate block">von {{ v.owner_name }}</span>
                    </div>
                    <Star v-if="v.is_default" class="w-3 h-3 text-amber-400 flex-shrink-0" :fill="'currentColor'" />
                    <span class="px-1.5 py-0.5 text-[9px] font-bold rounded-md flex-shrink-0" :class="v.role === 'editor' ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400' : 'bg-amber-100 dark:bg-amber-900/40 text-amber-600 dark:text-amber-400'">
                      {{ v.role === 'editor' ? 'Editor' : 'Leser' }}
                    </span>
                    <button
                      @click.stop="copyView(v.id)"
                      class="p-1 text-slate-400 hover:text-green-500 hover:bg-green-50 dark:hover:bg-green-900/30 rounded transition-colors flex-shrink-0"
                      title="Ansicht in eigene Ansichten kopieren"
                    >
                      <Copy class="w-3.5 h-3.5" />
                    </button>
                  </button>
                </div>
              </template>

              <!-- Actions -->
              <div class="p-1.5 border-t border-slate-100 dark:border-slate-700 mt-1">
                <button
                  @click="showCreateView = true; showViewSelector = false"
                  class="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors text-left"
                >
                  <Plus class="w-3.5 h-3.5 text-blue-500" />
                  <span class="text-sm font-medium text-blue-600 dark:text-blue-400">Neue Ansicht erstellen</span>
                </button>
              </div>
            </div>
          </Transition>
        </div>

        <!-- Read-only indicator for shared views -->
        <div v-if="activeView && activeView.role === 'reader'" class="hidden sm:flex items-center gap-1.5 px-2.5 py-1.5 bg-amber-50/80 dark:bg-amber-900/20 border border-amber-200/50 dark:border-amber-800/40 rounded-lg">
          <Lock class="w-3 h-3 text-amber-500" />
          <span class="text-[11px] font-semibold text-amber-700 dark:text-amber-300">Nur Lesen</span>
        </div>
        <div v-else-if="activeView && activeView.role === 'editor'" class="hidden sm:flex items-center gap-1.5 px-2.5 py-1.5 bg-blue-50/80 dark:bg-blue-900/20 border border-blue-200/50 dark:border-blue-800/40 rounded-lg">
          <Edit3 class="w-3 h-3 text-blue-500" />
          <span class="text-[11px] font-semibold text-blue-700 dark:text-blue-300">von {{ activeView.owner_name }}</span>
        </div>
      </div>

      <!-- View Actions -->
      <div class="flex items-center gap-0.5 sm:gap-1">
        <!-- DND / Pause Dropdown -->
        <div v-if="currentUserId" class="relative" ref="dndMenuRef">
          <button
            @click="showDndMenu = !showDndMenu"
            :disabled="isSettingPresence"
            class="relative p-2 rounded-lg transition-all duration-200"
            :class="isDnd
              ? 'text-red-500 bg-red-50 dark:bg-red-900/30 hover:bg-red-100 dark:hover:bg-red-900/50 ring-1 ring-red-200 dark:ring-red-800/50'
              : 'text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700'"
            :title="isDnd ? `${activeDndLabel} — Klicken für Optionen` : 'Status setzen'"
          >
            <Loader2 v-if="isSettingPresence" class="w-4 h-4 animate-spin" />
            <Coffee v-else-if="activeDndPreset === 'coffee'" class="w-4 h-4" />
            <UtensilsCrossed v-else-if="activeDndPreset === 'lunch'" class="w-4 h-4" />
            <BellOff v-else-if="isDnd" class="w-4 h-4" />
            <Bell v-else class="w-4 h-4" />
            <span v-if="isDnd" class="absolute -top-0.5 -right-0.5 w-2 h-2 bg-red-500 rounded-full ring-2 ring-white dark:ring-slate-800"></span>
          </button>

          <!-- Dropdown Menu -->
          <Transition
            enter-active-class="transition duration-150 ease-out"
            enter-from-class="opacity-0 scale-95 -translate-y-1"
            enter-to-class="opacity-100 scale-100 translate-y-0"
            leave-active-class="transition duration-100 ease-in"
            leave-from-class="opacity-100 scale-100"
            leave-to-class="opacity-0 scale-95"
          >
            <div
              v-if="showDndMenu && !isSettingPresence"
              class="absolute right-0 top-full mt-1.5 w-52 bg-white/95 dark:bg-slate-800/95 backdrop-blur-xl rounded-xl shadow-xl border border-slate-200/80 dark:border-slate-700/80 overflow-hidden z-50"
            >
              <!-- Clear status (when active) -->
              <button
                v-if="isDnd"
                @click="clearDnd"
                class="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-sm text-emerald-600 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 transition-colors"
              >
                <Bell class="w-4 h-4" />
                <span class="font-medium">Verfügbar</span>
                <span class="ml-auto text-[11px] text-slate-400">Zurücksetzen</span>
              </button>
              <div v-if="isDnd" class="border-t border-slate-100 dark:border-slate-700/60"></div>

              <!-- Kurze Pause -->
              <button
                @click="setDndPreset('coffee', 5)"
                class="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-sm transition-colors"
                :class="activeDndPreset === 'coffee'
                  ? 'text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/25'
                  : 'text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700/50'"
              >
                <Coffee class="w-4 h-4 text-amber-500" />
                <span class="font-medium">Kurze Pause</span>
                <span class="ml-auto text-[11px] text-slate-400">5 Min</span>
              </button>

              <!-- Mittagspause -->
              <button
                @click="setDndPreset('lunch', 30)"
                class="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-sm transition-colors"
                :class="activeDndPreset === 'lunch'
                  ? 'text-orange-700 dark:text-orange-300 bg-orange-50 dark:bg-orange-900/25'
                  : 'text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700/50'"
              >
                <UtensilsCrossed class="w-4 h-4 text-orange-500" />
                <span class="font-medium">Mittagspause</span>
                <span class="ml-auto text-[11px] text-slate-400">30 Min</span>
              </button>

              <!-- Nicht stören -->
              <button
                @click="setDndPreset('dnd', 480)"
                class="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-sm transition-colors"
                :class="activeDndPreset === 'dnd'
                  ? 'text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/25'
                  : 'text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700/50'"
              >
                <BellOff class="w-4 h-4 text-red-500" />
                <span class="font-medium">Nicht stören</span>
                <span class="ml-auto text-[11px] text-slate-400">8 Std</span>
              </button>
            </div>
          </Transition>
        </div>

        <div v-if="currentUserId" class="w-px h-5 bg-slate-200 dark:bg-slate-700 mx-0.5"></div>

        <!-- Monitor Mode Button (hidden on mobile) -->
        <button
          @click="emit('toggle-monitor-mode')"
          class="hidden md:block p-2 text-slate-400 hover:text-indigo-500 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 rounded-lg transition-colors"
          title="Monitor-Modus"
        >
          <Monitor class="w-4 h-4" />
        </button>

        <button
          v-if="isViewOwner"
          @click="openShareModal"
          class="p-2 text-slate-400 hover:text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg transition-colors"
          title="Ansicht teilen"
        >
          <Share2 class="w-4 h-4" />
        </button>
        <button
          @click="setDefaultView"
          class="p-2 rounded-lg transition-colors"
          :class="activeView?.is_default ? 'text-amber-400 bg-amber-50 dark:bg-amber-900/30' : 'text-slate-400 hover:text-amber-500 hover:bg-amber-50 dark:hover:bg-amber-900/30'"
          :title="activeView?.is_default ? 'Ist Standardansicht' : 'Als Standard festlegen'"
        >
          <Star class="w-4 h-4" :fill="activeView?.is_default ? 'currentColor' : 'none'" />
        </button>
        <button
          v-if="isViewOwner"
          @click="startEditViewName"
          class="p-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
          title="Ansicht umbenennen"
        >
          <Pencil class="w-3.5 h-3.5" />
        </button>
        <button
          v-if="isViewOwner && ownViews.length > 1"
          @click="showDeleteView = true"
          class="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors"
          title="Ansicht löschen"
        >
          <Trash2 class="w-3.5 h-3.5" />
        </button>
      </div>
    </div>

    <!-- Stats Toggle Bar -->
    <div class="flex items-center justify-between">
      <button
        @click="toggleDashboardStats"
        class="flex items-center gap-2 px-3 py-1.5 text-xs font-semibold rounded-lg transition-colors"
        :class="showDashboardStats
          ? 'text-slate-700 dark:text-slate-200 bg-slate-100 dark:bg-slate-700/50'
          : 'text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800'"
      >
        <BarChart3 class="w-3.5 h-3.5" />
        <span>Statistiken & Call Queue</span>
        <ChevronDown v-if="!showDashboardStats" class="w-3 h-3" />
        <ChevronUp v-else class="w-3 h-3" />
      </button>
    </div>

    <!-- Quick Actions Bar (collapsible) -->
    <Transition
      enter-active-class="transition-all duration-200 ease-out"
      enter-from-class="opacity-0 max-h-0 overflow-hidden"
      enter-to-class="opacity-100 max-h-[500px]"
      leave-active-class="transition-all duration-150 ease-in"
      leave-from-class="opacity-100 max-h-[500px]"
      leave-to-class="opacity-0 max-h-0 overflow-hidden"
    >
    <div v-show="showDashboardStats" class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">

      <!-- Presence Overview Card -->
      <div class="relative overflow-hidden bg-gradient-to-br from-emerald-500/10 via-emerald-400/5 to-transparent dark:from-emerald-500/20 dark:via-emerald-500/5 border border-emerald-200/50 dark:border-emerald-800/40 rounded-2xl p-4 group">
        <div class="flex items-center gap-3">
          <div class="w-10 h-10 rounded-xl bg-emerald-500/15 dark:bg-emerald-500/25 flex items-center justify-center">
            <Users class="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
          </div>
          <div>
            <p class="text-2xl font-bold text-emerald-700 dark:text-emerald-300">{{ presenceStats.available }}</p>
            <p class="text-xs text-emerald-600/70 dark:text-emerald-400/70 font-medium">Verfügbar</p>
          </div>
        </div>
        <div class="mt-3 flex flex-wrap items-center gap-2 sm:gap-3 text-[11px] font-medium">
          <span class="flex items-center gap-1 text-red-500"><span class="w-1.5 h-1.5 rounded-full bg-red-500"></span> {{ presenceStats.busy }} Besch.</span>
          <span class="flex items-center gap-1 text-amber-500"><span class="w-1.5 h-1.5 rounded-full bg-amber-500"></span> {{ presenceStats.away }} Abw.</span>
          <span class="flex items-center gap-1 text-slate-400"><span class="w-1.5 h-1.5 rounded-full bg-slate-400"></span> {{ presenceStats.offline }} Offline</span>
        </div>
      </div>

      <!-- Dashboard Stats Card -->
      <div class="relative overflow-hidden bg-gradient-to-br from-blue-500/10 via-blue-400/5 to-transparent dark:from-blue-500/20 dark:via-blue-500/5 border border-blue-200/50 dark:border-blue-800/40 rounded-2xl p-4 group">
        <div class="flex items-center gap-3">
          <div class="w-10 h-10 rounded-xl bg-blue-500/15 dark:bg-blue-500/25 flex items-center justify-center">
            <LayoutGrid class="w-5 h-5 text-blue-600 dark:text-blue-400" />
          </div>
          <div>
            <p class="text-2xl font-bold text-blue-700 dark:text-blue-300">{{ totalContacts }}</p>
            <p class="text-xs text-blue-600/70 dark:text-blue-400/70 font-medium">Kontakte im Dashboard</p>
          </div>
        </div>
        <div class="mt-3 flex items-center gap-3 text-[11px] font-medium text-blue-600/60 dark:text-blue-400/60">
          <span>{{ groups.length }} {{ groups.length === 1 ? 'Gruppe' : 'Gruppen' }}</span>
          <span>·</span>
          <span>{{ inCallCount }} im Anruf</span>
        </div>
      </div>

      <!-- Call Queue Settings Button -->
      <button
        @click="openCallQueueSettings"
        class="relative overflow-hidden bg-gradient-to-br from-violet-500/10 via-violet-400/5 to-transparent dark:from-violet-500/20 dark:via-violet-500/5 border border-violet-200/50 dark:border-violet-800/40 rounded-2xl p-4 text-left group hover:shadow-md transition-shadow duration-200 cursor-pointer"
      >
        <div class="flex items-center gap-3">
          <div class="w-10 h-10 rounded-xl bg-violet-500/15 dark:bg-violet-500/25 flex items-center justify-center">
            <Headphones class="w-5 h-5 text-violet-600 dark:text-violet-400" />
          </div>
          <div>
            <p class="text-sm font-semibold text-violet-700 dark:text-violet-300">Call Queue</p>
            <p class="text-xs text-violet-600/70 dark:text-violet-400/70 font-medium">An-/Abmelden</p>
          </div>
        </div>
        <div class="mt-3 flex items-center gap-1.5 text-[11px] font-medium text-violet-600/60 dark:text-violet-400/60">
          <ExternalLink class="w-3 h-3" />
          <span>Teams Einstellungen öffnen</span>
        </div>
      </button>
    </div>
    </Transition>

    <!-- Calls section ABOVE groups (when callsFirst) -->
    <template v-if="callsFirst">
      <div class="bg-white/90 dark:bg-slate-800/90 border border-slate-200/50 dark:border-slate-700/50 rounded-2xl shadow-sm" ref="callsSectionRef">
        <!-- Recent Calls Header -->
        <div class="flex flex-col sm:flex-row sm:items-center justify-between px-3 sm:px-4 py-2.5 sm:py-3 gap-2 sm:gap-0">
          <div class="flex items-center gap-2.5">
            <button
              @click="toggleCallsFirst"
              class="w-7 h-7 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center shadow-sm cursor-grab hover:shadow-md transition-shadow"
              :title="callsFirst ? 'Nach unten verschieben' : 'Nach oben verschieben'"
            >
              <PhoneCall class="w-3.5 h-3.5 text-white" />
            </button>
            <h2 class="text-sm font-bold text-slate-800 dark:text-slate-200">Letzte Anrufe</h2>
            <span class="px-2 py-0.5 bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400 text-[11px] font-bold rounded-full">{{ dashboardCallRecords.length }}</span>
            <button
              @click="toggleCallsFirst"
              class="p-1 rounded-md text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
              :title="callsFirst ? 'Nach unten verschieben' : 'Nach oben verschieben'"
            >
              <ChevronDown class="w-3.5 h-3.5" />
            </button>
          </div>
          <div class="flex items-center gap-2">
            <div class="flex gap-1 overflow-x-auto scrollbar-hide -mx-1 px-1">
              <button
                @click="switchDashboardCallTab('personal')"
                :class="['px-2.5 py-1 text-[11px] font-semibold rounded-lg whitespace-nowrap transition-all duration-200', dashboardCallTab === 'personal' ? 'bg-gradient-to-r from-slate-800 to-slate-700 dark:from-white dark:to-slate-100 text-white dark:text-slate-900 shadow-sm' : 'text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700/50']"
              >Persönlich</button>
              <button
                v-for="cq in dashboardCallQueues" :key="cq.queue_user_id"
                @click="switchDashboardCallTab(cq.queue_email)"
                :class="['px-2.5 py-1 text-[11px] font-semibold rounded-lg whitespace-nowrap transition-all duration-200', dashboardCallTab === cq.queue_email ? 'bg-gradient-to-r from-violet-600 to-purple-600 text-white shadow-sm' : 'text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700/50']"
              >{{ cq.queue_name || cq.queue_email }}</button>
            </div>
            <button @click="$emit('switch-tab', 'calls')" class="text-[11px] font-semibold text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors px-2 py-1 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/30" title="Alle Anrufe anzeigen">Alle anzeigen</button>
          </div>
        </div>
        <div class="px-2.5 sm:px-3 pb-2.5 sm:pb-3 max-h-[320px] overflow-y-auto scrollbar-thin scrollbar-thumb-slate-200 dark:scrollbar-thumb-slate-700 scrollbar-track-transparent">
          <div v-if="dashboardCallsLoading" class="flex items-center justify-center py-6"><Loader2 class="w-5 h-5 text-blue-500 animate-spin" /></div>
          <div v-else-if="dashboardCallRecords.length === 0" class="py-6 text-center"><Phone class="w-5 h-5 mx-auto mb-2 text-slate-300 dark:text-slate-500" /><p class="text-xs text-slate-400 dark:text-slate-500 font-medium">Keine Anrufe vorhanden</p></div>
          <div v-else class="space-y-1.5">
            <CallCard v-for="call in dashboardCallRecords" :key="call.id" :call="call" :currentUserId="currentUserId" :hasNote="!!call.note_status" :isQueueView="dashboardCallTab !== 'personal'" @open-contact="(email) => $emit('open-contact', email)" @open-notes="(call) => $emit('open-notes', call)" />
          </div>
        </div>
      </div>
    </template>

    <!-- Groups Section -->
    <div class="space-y-4">

    <!-- Groups Header -->
    <div class="flex items-center justify-between">
      <div class="flex items-center gap-2">
        <h2 class="text-sm font-semibold text-slate-700 dark:text-slate-300">Gruppen</h2>
        <span class="px-2 py-0.5 bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400 text-[11px] font-bold rounded-full">{{ groups.length }}</span>
      </div>
      <button
        v-if="canEdit"
        @click="showAddGroup = true"
        class="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold bg-slate-900 dark:bg-white text-white dark:text-slate-900 rounded-xl hover:bg-slate-700 dark:hover:bg-slate-200 transition-colors shadow-sm hover:shadow-md active:translate-y-0"
      >
        <Plus class="w-3.5 h-3.5" />
        Neue Gruppe
      </button>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="flex flex-col items-center justify-center py-16">
      <div class="relative">
        <div class="w-12 h-12 rounded-2xl bg-gradient-to-br from-blue-500 to-violet-500 animate-pulse"></div>
        <Loader2 class="w-6 h-6 text-white absolute top-3 left-3 animate-spin" />
      </div>
      <p class="text-slate-400 text-sm mt-4 font-medium">Dashboard laden...</p>
    </div>

    <!-- Empty State -->
    <div v-else-if="groups.length === 0" class="flex flex-col items-center justify-center py-16">
      <div class="relative mb-6">
        <div class="w-20 h-20 rounded-3xl bg-gradient-to-br from-slate-100 to-slate-200 dark:from-slate-700 dark:to-slate-800 flex items-center justify-center shadow-inner">
          <LayoutGrid class="w-10 h-10 text-slate-300 dark:text-slate-600" />
        </div>
        <div class="absolute -bottom-1 -right-1 w-8 h-8 rounded-xl bg-gradient-to-br from-blue-500 to-violet-500 flex items-center justify-center shadow-lg">
          <Plus class="w-4 h-4 text-white" />
        </div>
      </div>
      <h3 class="text-lg font-bold text-slate-800 dark:text-slate-200 mb-2">Dein Dashboard ist leer</h3>
      <p class="text-slate-500 dark:text-slate-400 mb-6 max-w-sm mx-auto text-center text-sm leading-relaxed">
        Erstelle Gruppen und ordne Kontakte zu, um Teams-Status und Erreichbarkeit auf einen Blick zu sehen.
      </p>
      <button
        v-if="canEdit"
        @click="showAddGroup = true"
        class="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-slate-900 to-slate-800 dark:from-white dark:to-slate-100 text-white dark:text-slate-900 rounded-xl font-semibold text-sm shadow-lg hover:shadow-xl transition-shadow duration-200"
      >
        <Plus class="w-4 h-4" />
        Erste Gruppe erstellen
      </button>
    </div>

    <!-- Groups Grid -->
    <div v-else class="space-y-4">
      <div
        v-for="(group, groupIndex) in groups"
        :key="group.id"
        class="relative bg-white/90 dark:bg-slate-800/90 border border-slate-200/50 dark:border-slate-700/50 rounded-2xl shadow-sm hover:shadow-md transition-all duration-200"
        :class="{
          'ring-2 ring-blue-400/60 shadow-lg shadow-blue-500/10 scale-[1.01]': dragOverGroupId === group.id && dragType === 'contact',
          'border-t-2 border-t-blue-500': dragOverGroupIndex === groupIndex && dragType === 'group',
          'opacity-40': draggedGroupId === group.id,
        }"
        @dragover.prevent="onDragOver($event, group.id, groupIndex)"
        @dragleave="onDragLeave(group.id, groupIndex)"
        @drop.prevent="onDrop($event, group.id, groupIndex)"
      >
        <!-- Group Header -->
        <div class="flex items-center justify-between px-3 sm:px-4 py-2.5 sm:py-3">
          <div class="flex items-center gap-2.5 flex-1 min-w-0">
            <!-- Group Drag Handle -->
            <div
              v-if="canEdit"
              class="cursor-grab active:cursor-grabbing text-slate-300 dark:text-slate-600 hover:text-slate-500 dark:hover:text-slate-400 flex-shrink-0 -ml-1"
              draggable="true"
              @dragstart.stop="onGroupDragStart($event, group)"
              @dragend="onGroupDragEnd"
              title="Gruppe verschieben"
            >
              <GripVertical class="w-4 h-4" />
            </div>
            <div class="w-2.5 h-2.5 rounded-full flex-shrink-0" :class="group.color || groupColors[groupIndex % groupColors.length]"></div>

            <!-- Color Picker -->
            <div class="relative" @click.stop>
              <button
                @click="toggleColorPicker(group.id)"
                class="p-0.5 rounded hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors"
                title="Farbe wählen"
              >
                <Palette class="w-3.5 h-3.5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300" />
              </button>
              <div v-if="colorPickerGroupId === group.id" class="absolute left-0 top-full mt-1 z-50 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg p-2 flex gap-1.5">
                <button
                  v-for="c in groupColors"
                  :key="c"
                  @click="setGroupColor(group, c)"
                  class="w-5 h-5 rounded-full transition-transform hover:scale-125"
                  :class="[c, group.color === c ? 'ring-2 ring-offset-1 ring-slate-500 dark:ring-slate-300' : '']"
                />
              </div>
            </div>

            <div v-if="editingGroupId === group.id" class="flex items-center gap-2 flex-1">
              <input
                ref="editInputRefs"
                v-model="editGroupName"
                @keydown.enter="saveGroupName(group.id)"
                @keydown.escape="cancelEdit"
                @blur="saveGroupName(group.id)"
                class="flex-1 px-2.5 py-1 text-sm font-semibold bg-white dark:bg-slate-900 border border-blue-300 dark:border-blue-600 rounded-lg focus:ring-2 focus:ring-blue-500/30 text-slate-800 dark:text-slate-100 outline-none"
                autofocus
              />
            </div>
            <h3
              v-else
              @dblclick="startEdit(group)"
              class="text-sm font-bold text-slate-800 dark:text-slate-200 truncate cursor-pointer hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
              title="Doppelklick zum Umbenennen"
            >
              {{ group.group_name }}
            </h3>

            <span class="px-1.5 py-0.5 bg-slate-100 dark:bg-slate-700 text-slate-400 text-[10px] font-bold rounded-md flex-shrink-0">{{ group.contacts?.length || 0 }}</span>
          </div>

          <div class="flex items-center gap-0.5">
            <div v-if="!group.is_collapsed" class="hidden sm:flex items-center gap-0.5 mr-2">
              <div
                v-for="status in getGroupPresenceSummary(group)"
                :key="status.class"
                class="w-1.5 h-1.5 rounded-full"
                :class="status.class"
                :title="`${status.count} ${status.label}`"
              ></div>
            </div>
            <button
              v-if="canEdit"
              @click="openAddContact(group.id)"
              class="p-1.5 text-slate-400 hover:text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg transition-colors"
              title="Kontakt hinzufügen"
            >
              <UserPlus class="w-3.5 sm:w-4 h-3.5 sm:h-4" />
            </button>
            <button
              v-if="canEdit"
              @click="startEdit(group)"
              class="hidden sm:block p-1.5 text-slate-400 hover:text-amber-500 hover:bg-amber-50 dark:hover:bg-amber-900/30 rounded-lg transition-colors"
              title="Gruppe umbenennen"
            >
              <Pencil class="w-3.5 h-3.5" />
            </button>
            <button
              @click="toggleCollapse(group)"
              class="p-1.5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 rounded-lg transition-colors"
            >
              <ChevronDown v-if="group.is_collapsed" class="w-4 h-4 transition-transform" />
              <ChevronUp v-else class="w-4 h-4 transition-transform" />
            </button>
            <button
              v-if="canEdit"
              @click="askDeleteGroup(group)"
              class="hidden sm:block p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors"
              title="Gruppe löschen"
            >
              <Trash2 class="w-3.5 h-3.5" />
            </button>
          </div>
        </div>

        <!-- Group Contacts -->
        <div
          v-show="!group.is_collapsed"
          class="px-3 sm:px-4 pb-3 sm:pb-4"
        >
          <div
            v-if="!group.contacts || group.contacts.length === 0"
            class="py-8 text-center border-2 border-dashed border-slate-200/80 dark:border-slate-700/60 rounded-xl bg-slate-50/50 dark:bg-slate-900/20"
          >
            <UserPlus class="w-6 h-6 mx-auto mb-2 text-slate-300 dark:text-slate-600" />
            <p class="text-sm text-slate-400 dark:text-slate-500">
              Kontakte hierher ziehen oder
              <button @click="openAddContact(group.id)" class="text-blue-500 hover:text-blue-600 font-medium underline underline-offset-2 decoration-blue-300">hinzufügen</button>
            </p>
          </div>

          <div v-else class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-1.5 sm:gap-2">
            <div
              v-for="(contact, contactIndex) in group.contacts"
              :key="contact.contact_email"
              class="relative flex items-center gap-2.5 sm:gap-3 p-2.5 sm:p-3 rounded-xl border transition-colors duration-150 cursor-pointer group/contact"
              :class="[
                isInCall(contact.entra_id) ? 'bg-red-50/40 dark:bg-red-900/10 border-red-200/30 dark:border-red-800/20' :
                isInMeeting(contact.entra_id) && !isInCall(contact.entra_id) ? 'bg-purple-50/30 dark:bg-purple-900/10 border-purple-200/30 dark:border-purple-800/20' :
                dragOverContactGroupId === group.id && dragOverContactIdx === contactIndex
                  ? 'border-blue-400 dark:border-blue-500 bg-blue-50/30 dark:bg-blue-900/10'
                  : 'border-transparent hover:border-slate-200/80 dark:hover:border-slate-600/50 hover:bg-white/80 dark:hover:bg-slate-700/30'
              ]"
              :draggable="canEdit"
              @dragstart="canEdit && onContactDragStart($event, contact, group.id)"
              @dragend="onContactDragEnd"
              @dragover.prevent.stop="onContactDragOver($event, group.id, contactIndex)"
              @dragleave.stop="onContactDragLeave(group.id, contactIndex)"
              @drop.prevent.stop="onContactDrop($event, group.id, contactIndex)"
              @click="$emit('open-contact', contact.contact_email)"
            >
              <div class="relative flex-shrink-0">
                <img
                  v-if="contactPhotos[contact.contact_email]"
                  :src="contactPhotos[contact.contact_email]"
                  :alt="contact.display_name"
                  class="w-10 h-10 rounded-xl object-cover ring-2 ring-white dark:ring-slate-800 shadow-sm"
                >
                <div
                  v-else
                  class="w-10 h-10 rounded-xl flex items-center justify-center shadow-sm ring-2 ring-white dark:ring-slate-800"
                  :class="avatarGradients[getInitials(contact.display_name).charCodeAt(0) % avatarGradients.length]"
                >
                  <span class="text-xs font-bold text-white drop-shadow-sm">{{ getInitials(contact.display_name) }}</span>
                </div>
                <div
                  v-if="contact.entra_id"
                  class="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full border-[2.5px] border-white dark:border-slate-800"
                  :class="[getPresenceClass(contact.entra_id), { 'animate-pulse': isInCall(contact.entra_id) }]"
                  :title="getPresenceLabel(contact.entra_id)"
                ></div>
              </div>

              <div class="flex-1 min-w-0">
                <div class="flex items-center gap-1.5">
                  <span class="text-sm font-semibold text-slate-800 dark:text-slate-200 truncate">
                    {{ contact.display_name || contact.contact_email }}
                  </span>
                </div>
                <div class="flex items-center gap-1.5 mt-0.5">
                  <span
                    v-if="isInCall(contact.entra_id)"
                    class="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-bold bg-red-100 dark:bg-red-900/50 text-red-600 dark:text-red-400 rounded-md"
                  >
                    <Phone class="w-2.5 h-2.5 animate-pulse" />
                    Im Anruf
                  </span>
                  <span
                    v-else-if="isInMeeting(contact.entra_id)"
                    class="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-bold bg-purple-100 dark:bg-purple-900/40 text-purple-600 dark:text-purple-400 rounded-md"
                  >
                    <Video class="w-2.5 h-2.5" />
                    Im Meeting
                  </span>
                  <span
                    v-else-if="contact.job_title"
                    class="text-xs text-slate-500 dark:text-slate-400 truncate"
                  >{{ contact.job_title }}</span>
                  <span
                    v-else-if="contact.company_name"
                    class="text-xs text-slate-500 dark:text-slate-400 truncate"
                  >{{ contact.company_name }}</span>
                  <span
                    v-else
                    class="text-xs text-slate-400 dark:text-slate-500 truncate"
                  >{{ getPresenceLabel(contact.entra_id) }}</span>
                </div>
              </div>

              <div class="absolute right-1.5 sm:right-2 top-1/2 -translate-y-1/2 flex items-center gap-0.5 sm:opacity-0 group-hover/contact:opacity-100 transition-opacity duration-150 bg-white/95 dark:bg-slate-800/95 rounded-lg p-0.5 shadow-sm border border-slate-200/50 dark:border-slate-600/50">
                <button
                  v-if="hasValidEmail(contact)"
                  @click.stop="openTeamsChat(contact)"
                  class="p-1.5 text-blue-500 hover:bg-blue-100 dark:hover:bg-blue-900/40 rounded-md transition-colors"
                  title="Teams Chat"
                >
                  <MessageSquare class="w-3.5 h-3.5" />
                </button>
                <!-- Call Button (favorite shortcut or dropdown) -->
                <button
                  v-if="hasAnyCallOption(contact)"
                  @click.stop="handleCallClick($event, contact)"
                  class="p-1.5 rounded-md transition-colors flex items-center gap-0.5"
                  :class="getCallPreference(contact.contact_email) ? 'text-emerald-600 hover:bg-emerald-100 dark:hover:bg-emerald-900/40' : 'text-emerald-500 hover:bg-emerald-100 dark:hover:bg-emerald-900/40'"
                  :title="getCallPreference(contact.contact_email) ? getCallPrefLabel(contact) : 'Anrufen'"
                >
                  <Heart v-if="getCallPreference(contact.contact_email)" class="w-2 h-2 text-emerald-400 fill-emerald-400" />
                  <PhoneCall class="w-3.5 h-3.5" />
                  <ChevronDown v-if="!getCallPreference(contact.contact_email) && hasMultipleCallOptions(contact)" class="w-2.5 h-2.5" />
                </button>
                <button
                  v-if="canEdit"
                  @click.stop="removeContact(group.id, contact.contact_email)"
                  class="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-100 dark:hover:bg-red-900/40 rounded-md transition-colors"
                  title="Entfernen"
                >
                  <X class="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    </div>

    <!-- Recent Calls BELOW groups (when NOT callsFirst) -->
    <div v-if="!callsFirst" class="bg-white/90 dark:bg-slate-800/90 border border-slate-200/50 dark:border-slate-700/50 rounded-2xl shadow-sm">

      <!-- Header -->
      <div class="flex flex-col sm:flex-row sm:items-center justify-between px-3 sm:px-4 py-2.5 sm:py-3 gap-2 sm:gap-0">
        <div class="flex items-center gap-2.5">
          <button
            @click="toggleCallsFirst"
            class="w-7 h-7 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center shadow-sm cursor-grab hover:shadow-md transition-shadow"
            :title="callsFirst ? 'Nach unten verschieben' : 'Nach oben verschieben'"
          >
            <PhoneCall class="w-3.5 h-3.5 text-white" />
          </button>
          <h2 class="text-sm font-bold text-slate-800 dark:text-slate-200">Letzte Anrufe</h2>
          <span class="px-2 py-0.5 bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400 text-[11px] font-bold rounded-full">{{ dashboardCallRecords.length }}</span>
          <button
            @click="toggleCallsFirst"
            class="p-1 rounded-md text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
            :title="callsFirst ? 'Nach unten verschieben' : 'Nach oben verschieben'"
          >
            <ChevronUp class="w-3.5 h-3.5" />
          </button>
        </div>
        <div class="flex items-center gap-2">
          <!-- Call Tabs -->
          <div class="flex gap-1 overflow-x-auto scrollbar-hide -mx-1 px-1">
            <button
              @click="switchDashboardCallTab('personal')"
              :class="[
                'px-2.5 py-1 text-[11px] font-semibold rounded-lg whitespace-nowrap transition-all duration-200',
                dashboardCallTab === 'personal'
                  ? 'bg-gradient-to-r from-slate-800 to-slate-700 dark:from-white dark:to-slate-100 text-white dark:text-slate-900 shadow-sm'
                  : 'text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700/50'
              ]"
            >
              Persönlich
            </button>
            <button
              v-for="cq in dashboardCallQueues"
              :key="cq.queue_user_id"
              @click="switchDashboardCallTab(cq.queue_email)"
              :class="[
                'px-2.5 py-1 text-[11px] font-semibold rounded-lg whitespace-nowrap transition-all duration-200',
                dashboardCallTab === cq.queue_email
                  ? 'bg-gradient-to-r from-violet-600 to-purple-600 text-white shadow-sm'
                  : 'text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700/50'
              ]"
            >
              {{ cq.queue_name || cq.queue_email }}
            </button>
          </div>
          <button
            @click="$emit('switch-tab', 'calls')"
            class="text-[11px] font-semibold text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors px-2 py-1 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/30"
            title="Alle Anrufe anzeigen"
          >
            Alle anzeigen
          </button>
        </div>
      </div>

      <!-- Calls List (compact, max ~4 visible, scrollable) -->
      <div class="px-2.5 sm:px-3 pb-2.5 sm:pb-3 max-h-[320px] overflow-y-auto scrollbar-thin scrollbar-thumb-slate-200 dark:scrollbar-thumb-slate-700 scrollbar-track-transparent">
        <!-- Loading -->
        <div v-if="dashboardCallsLoading" class="flex items-center justify-center py-6">
          <Loader2 class="w-5 h-5 text-blue-500 animate-spin" />
        </div>

        <!-- Empty State -->
        <div v-else-if="dashboardCallRecords.length === 0" class="py-6 text-center">
          <Phone class="w-5 h-5 mx-auto mb-2 text-slate-300 dark:text-slate-500" />
          <p class="text-xs text-slate-400 dark:text-slate-500 font-medium">Keine Anrufe vorhanden</p>
        </div>

        <!-- Call Cards -->
        <div v-else class="space-y-1.5">
          <CallCard
            v-for="call in dashboardCallRecords"
            :key="call.id"
            :call="call"
            :currentUserId="currentUserId"
            :hasNote="!!call.note_status"
            :isQueueView="dashboardCallTab !== 'personal'"
            @open-contact="(email) => $emit('open-contact', email)"
            @open-notes="(call) => $emit('open-notes', call)"
          />
        </div>
      </div>
    </div>

    <!-- Call Menu Dropdown (Teleported to body for z-index safety) -->
    <Teleport to="body">
      <Transition
        enter-active-class="transition-all duration-150 ease-out"
        enter-from-class="opacity-0 scale-95"
        enter-to-class="opacity-100 scale-100"
        leave-active-class="transition-all duration-100 ease-in"
        leave-from-class="opacity-100 scale-100"
        leave-to-class="opacity-0 scale-95"
      >
        <div
          v-if="activeCallMenu"
          class="fixed z-[9999]"
          :style="callMenuPosition"
          @click.stop
        >
          <div class="bg-white dark:bg-slate-800 border border-slate-200/80 dark:border-slate-600/80 rounded-xl shadow-2xl shadow-black/20 py-1.5 min-w-[220px]">
            <button
              v-if="hasValidEmail(activeCallMenuContact)"
              @click.stop="openTeamsCall(activeCallMenuContact); closeCallMenu()"
              class="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700/60 transition-colors"
            >
              <div class="w-7 h-7 rounded-lg bg-purple-100 dark:bg-purple-900/40 flex items-center justify-center">
                <PhoneCall class="w-3.5 h-3.5 text-purple-600 dark:text-purple-400" />
              </div>
              <span class="font-medium flex-1 text-left">Teams Anruf</span>
              <Heart v-if="getCallPreference(activeCallMenuContact?.contact_email) === 'teams'" class="w-3 h-3 text-red-500 fill-red-500" />
            </button>
            <button
              v-if="getBusinessPhone(activeCallMenuContact)"
              @click.stop="callNumber(getBusinessPhone(activeCallMenuContact)); closeCallMenu()"
              class="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700/60 transition-colors"
            >
              <div class="w-7 h-7 rounded-lg bg-green-100 dark:bg-green-900/40 flex items-center justify-center">
                <Phone class="w-3.5 h-3.5 text-green-600 dark:text-green-400" />
              </div>
              <div class="flex flex-col items-start flex-1">
                <span class="font-medium">Telefon</span>
                <span class="text-[11px] text-slate-400 dark:text-slate-500">{{ getBusinessPhone(activeCallMenuContact) }}</span>
              </div>
              <Heart v-if="getCallPreference(activeCallMenuContact?.contact_email) === 'business'" class="w-3 h-3 text-red-500 fill-red-500" />
            </button>
            <button
              v-if="getMobilePhone(activeCallMenuContact)"
              @click.stop="callNumber(getMobilePhone(activeCallMenuContact)); closeCallMenu()"
              class="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700/60 transition-colors"
            >
              <div class="w-7 h-7 rounded-lg bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center">
                <Smartphone class="w-3.5 h-3.5 text-blue-600 dark:text-blue-400" />
              </div>
              <div class="flex flex-col items-start flex-1">
                <span class="font-medium">Mobil</span>
                <span class="text-[11px] text-slate-400 dark:text-slate-500">{{ getMobilePhone(activeCallMenuContact) }}</span>
              </div>
              <Heart v-if="getCallPreference(activeCallMenuContact?.contact_email) === 'mobile'" class="w-3 h-3 text-red-500 fill-red-500" />
            </button>
          </div>
        </div>
      </Transition>
    </Teleport>

    <!-- Add Group Modal -->
    <Teleport to="body">
      <Transition
        enter-active-class="transition-all duration-200 ease-out"
        enter-from-class="opacity-0"
        enter-to-class="opacity-100"
        leave-active-class="transition-all duration-150 ease-in"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
      >
        <div
          v-if="showAddGroup"
          class="fixed inset-0 z-50 flex items-center justify-center p-4"
          @click.self="showAddGroup = false"
        >
          <div class="absolute inset-0 bg-black/50"></div>
          <Transition
            enter-active-class="transition-all duration-200 ease-out"
            enter-from-class="opacity-0 scale-95 translate-y-4"
            enter-to-class="opacity-100 scale-100 translate-y-0"
            leave-active-class="transition-all duration-150 ease-in"
            leave-from-class="opacity-100 scale-100"
            leave-to-class="opacity-0 scale-95"
          >
            <div v-if="showAddGroup" class="relative bg-white dark:bg-slate-800 rounded-2xl shadow-2xl max-w-sm w-full p-6 border border-white/20 dark:border-slate-700/50">
              <div class="flex items-center gap-3 mb-4">
                <div class="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-violet-500 flex items-center justify-center shadow-lg shadow-blue-500/20">
                  <Plus class="w-5 h-5 text-white" />
                </div>
                <h3 class="text-lg font-bold text-slate-900 dark:text-slate-100">Neue Gruppe</h3>
              </div>
              <input
                v-model="newGroupName"
                @keydown.enter="createGroup"
                @keydown.escape="showAddGroup = false"
                placeholder="Gruppenname eingeben..."
                class="w-full px-4 py-3 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-600 rounded-xl focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 text-slate-800 dark:text-slate-100 text-sm outline-none transition-all"
                autofocus
              />
              <div class="flex gap-3 justify-end mt-5">
                <button
                  @click="showAddGroup = false"
                  class="px-4 py-2.5 text-sm font-semibold text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-xl transition-colors"
                >
                  Abbrechen
                </button>
                <button
                  @click="createGroup"
                  :disabled="!newGroupName.trim()"
                  class="px-5 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-slate-900 to-slate-800 dark:from-white dark:to-slate-100 dark:text-slate-900 rounded-xl transition-all shadow-md hover:shadow-lg disabled:opacity-40 disabled:shadow-none"
                >
                  Erstellen
                </button>
              </div>
            </div>
          </Transition>
        </div>
      </Transition>
    </Teleport>

    <!-- Add Contact Modal -->
    <Teleport to="body">
      <Transition
        enter-active-class="transition-all duration-200 ease-out"
        enter-from-class="opacity-0"
        enter-to-class="opacity-100"
        leave-active-class="transition-all duration-150 ease-in"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
      >
        <div
          v-if="showAddContact"
          class="fixed inset-0 z-50 flex items-start justify-center p-4 pt-[12vh]"
          @click.self="closeAddContact"
        >
          <div class="absolute inset-0 bg-black/50"></div>
          <div class="relative bg-white dark:bg-slate-800 rounded-2xl shadow-2xl max-w-md w-full border border-white/20 dark:border-slate-700/50 overflow-hidden">
            <div class="p-4 border-b border-slate-100 dark:border-slate-700">
              <h3 class="text-sm font-bold text-slate-900 dark:text-slate-100 mb-3">Kontakt hinzufügen</h3>

              <!-- Source filter pills (only if multiple sources available) -->
              <div v-if="availableSources.length > 1" class="flex flex-wrap gap-1.5 mb-3">
                <button
                  v-for="source in availableSources"
                  :key="source.id"
                  @click="toggleSourceFilter(source.id)"
                  class="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-lg border transition-all duration-150 select-none"
                  :title="contactSourceFilter.includes(source.id) ? 'Klicken um zu deaktivieren' : 'Klicken um zu aktivieren'"
                  :class="contactSourceFilter.includes(source.id)
                    ? source.source_type === 'entra_id'
                      ? 'bg-purple-100 dark:bg-purple-900/40 border-purple-300 dark:border-purple-700 text-purple-700 dark:text-purple-300'
                      : 'bg-blue-100 dark:bg-blue-900/40 border-blue-300 dark:border-blue-700 text-blue-700 dark:text-blue-300'
                    : 'bg-transparent border-slate-200 dark:border-slate-600 text-slate-400 dark:text-slate-500'"
                >
                  <Users v-if="source.source_type === 'entra_id'" class="w-3 h-3" />
                  <Globe v-else class="w-3 h-3" />
                  {{ source.display_name }}
                  <span 
                    v-if="!contactSourceFilter.includes(source.id)"
                    class="text-[10px] opacity-60"
                  >aus</span>
                </button>
              </div>

              <div class="relative">
                <Search class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  v-model="contactSearch"
                  @input="debouncedContactSearch"
                  placeholder="Name oder Email suchen..."
                  class="w-full pl-10 pr-4 py-2.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-600 rounded-xl focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 text-slate-800 dark:text-slate-100 text-sm outline-none transition-all"
                  autofocus
                />
              </div>
            </div>
            <div class="max-h-[40vh] overflow-y-auto p-2">
              <div v-if="contactSearchLoading" class="flex items-center justify-center py-10">
                <Loader2 class="w-5 h-5 text-blue-500 animate-spin" />
              </div>
              <div v-else-if="contactSearchResults.length === 0 && contactSearch.length >= 2" class="py-10 text-center text-sm text-slate-400">
                Keine Kontakte gefunden
              </div>
              <div v-else-if="contactSearch.length < 2" class="py-10 text-center text-sm text-slate-400">
                Mindestens 2 Zeichen eingeben
              </div>
              <button
                v-for="c in contactSearchResults"
                :key="c.email"
                @click="addContactToGroup(c.email)"
                class="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors text-left"
                :class="{ 'opacity-40 cursor-not-allowed': isContactInGroup(c.email, addContactGroupId) }"
                :disabled="isContactInGroup(c.email, addContactGroupId)"
              >
                <div class="relative flex-shrink-0">
                  <div
                    class="w-9 h-9 rounded-lg flex items-center justify-center"
                    :class="avatarGradients[getInitials(c.display_name).charCodeAt(0) % avatarGradients.length]"
                  >
                    <span class="text-xs font-bold text-white">{{ getInitials(c.display_name) }}</span>
                  </div>
                  <div
                    v-if="c.entra_id"
                    class="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-white dark:border-slate-800"
                    :class="getPresenceClass(c.entra_id)"
                  ></div>
                </div>
                <div class="flex-1 min-w-0">
                  <div class="flex items-center gap-1.5">
                    <span class="text-sm font-semibold text-slate-800 dark:text-slate-200 truncate">{{ c.display_name }}</span>
                    <span
                      v-if="c.is_entra_user"
                      class="flex-shrink-0 text-[9px] font-bold px-1.5 py-0.5 rounded bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300"
                    >Teams</span>
                    <span
                      v-else
                      class="flex-shrink-0 text-[9px] font-bold px-1.5 py-0.5 rounded bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300"
                    >Extern</span>
                  </div>
                  <div class="text-xs text-slate-500 truncate">{{ c.job_title || c.email }}</div>
                </div>
                <div v-if="isContactInGroup(c.email, addContactGroupId)" class="text-[10px] font-semibold text-slate-400 bg-slate-100 dark:bg-slate-700 px-2 py-0.5 rounded-md">Vorhanden</div>
                <Plus v-else class="w-4 h-4 text-slate-400" />
              </button>
            </div>
            <div class="p-3 border-t border-slate-100 dark:border-slate-700 flex justify-end">
              <button
                @click="closeAddContact"
                class="px-4 py-2 text-sm font-semibold text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-xl transition-colors"
              >
                Schließen
              </button>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>

    <!-- Delete Group Confirmation Modal -->
    <Teleport to="body">
      <Transition
        enter-active-class="transition-all duration-200 ease-out"
        enter-from-class="opacity-0"
        enter-to-class="opacity-100"
        leave-active-class="transition-all duration-150 ease-in"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
      >
        <div
          v-if="showDeleteGroup"
          class="fixed inset-0 z-50 flex items-center justify-center p-4"
          @click.self="showDeleteGroup = false"
        >
          <div class="absolute inset-0 bg-black/50"></div>
          <div class="relative bg-white dark:bg-slate-800 rounded-2xl shadow-2xl max-w-sm w-full p-6 border border-white/20 dark:border-slate-700/50">
            <div class="flex items-center gap-3 mb-4">
              <div class="w-10 h-10 rounded-xl bg-red-100 dark:bg-red-900/40 flex items-center justify-center">
                <Trash2 class="w-5 h-5 text-red-600 dark:text-red-400" />
              </div>
              <h3 class="text-lg font-bold text-slate-900 dark:text-slate-100">Gruppe löschen?</h3>
            </div>
            <p class="text-slate-600 dark:text-slate-400 mb-6 text-sm leading-relaxed">
              Möchtest du die Gruppe <strong class="text-slate-900 dark:text-slate-100">{{ deleteGroupTarget?.group_name }}</strong>
              mit {{ deleteGroupTarget?.contacts?.length || 0 }} Kontakten unwiderruflich löschen?
            </p>
            <div class="flex gap-3 justify-end">
              <button
                @click="showDeleteGroup = false"
                class="px-4 py-2.5 text-sm font-semibold text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-xl transition-colors"
              >
                Abbrechen
              </button>
              <button
                @click="confirmDeleteGroup"
                class="px-4 py-2.5 text-sm font-semibold text-white bg-red-600 hover:bg-red-700 rounded-xl transition-all shadow-md hover:shadow-lg shadow-red-500/20"
              >
                Löschen
              </button>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>

    <!-- Create View Modal -->
    <Teleport to="body">
      <Transition
        enter-active-class="transition-all duration-200 ease-out"
        enter-from-class="opacity-0"
        enter-to-class="opacity-100"
        leave-active-class="transition-all duration-150 ease-in"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
      >
        <div
          v-if="showCreateView"
          class="fixed inset-0 z-50 flex items-center justify-center p-4"
          @click.self="showCreateView = false"
        >
          <div class="absolute inset-0 bg-black/50"></div>
          <div class="relative bg-white dark:bg-slate-800 rounded-2xl shadow-2xl max-w-sm w-full p-6 border border-white/20 dark:border-slate-700/50">
            <div class="flex items-center gap-3 mb-4">
              <div class="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-violet-500 flex items-center justify-center shadow-lg shadow-blue-500/20">
                <LayoutGrid class="w-5 h-5 text-white" />
              </div>
              <h3 class="text-lg font-bold text-slate-900 dark:text-slate-100">Neue Ansicht</h3>
            </div>
            <input
              v-model="newViewName"
              @keydown.enter="createView"
              @keydown.escape="showCreateView = false"
              placeholder="Name der Ansicht..."
              class="w-full px-4 py-3 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-600 rounded-xl focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 text-slate-800 dark:text-slate-100 text-sm outline-none transition-all"
              autofocus
            />
            <div class="flex gap-3 justify-end mt-5">
              <button
                @click="showCreateView = false"
                class="px-4 py-2.5 text-sm font-semibold text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-xl transition-colors"
              >
                Abbrechen
              </button>
              <button
                @click="createView"
                :disabled="!newViewName.trim()"
                class="px-5 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-slate-900 to-slate-800 dark:from-white dark:to-slate-100 dark:text-slate-900 rounded-xl transition-all shadow-md hover:shadow-lg disabled:opacity-40 disabled:shadow-none"
              >
                Erstellen
              </button>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>

    <!-- Rename View Modal -->
    <Teleport to="body">
      <Transition
        enter-active-class="transition-all duration-200 ease-out"
        enter-from-class="opacity-0"
        enter-to-class="opacity-100"
        leave-active-class="transition-all duration-150 ease-in"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
      >
        <div
          v-if="editingViewName"
          class="fixed inset-0 z-50 flex items-center justify-center p-4"
          @click.self="editingViewName = false"
        >
          <div class="absolute inset-0 bg-black/50"></div>
          <div class="relative bg-white dark:bg-slate-800 rounded-2xl shadow-2xl max-w-sm w-full p-6 border border-white/20 dark:border-slate-700/50">
            <div class="flex items-center gap-3 mb-4">
              <div class="w-10 h-10 rounded-xl bg-amber-100 dark:bg-amber-900/40 flex items-center justify-center">
                <Pencil class="w-5 h-5 text-amber-600 dark:text-amber-400" />
              </div>
              <h3 class="text-lg font-bold text-slate-900 dark:text-slate-100">Ansicht umbenennen</h3>
            </div>
            <input
              v-model="editViewNameValue"
              @keydown.enter="renameView"
              @keydown.escape="editingViewName = false"
              class="w-full px-4 py-3 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-600 rounded-xl focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 text-slate-800 dark:text-slate-100 text-sm outline-none transition-all"
              autofocus
            />
            <div class="flex gap-3 justify-end mt-5">
              <button
                @click="editingViewName = false"
                class="px-4 py-2.5 text-sm font-semibold text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-xl transition-colors"
              >
                Abbrechen
              </button>
              <button
                @click="renameView"
                :disabled="!editViewNameValue.trim()"
                class="px-5 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-slate-900 to-slate-800 dark:from-white dark:to-slate-100 dark:text-slate-900 rounded-xl transition-all shadow-md hover:shadow-lg disabled:opacity-40"
              >
                Speichern
              </button>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>

    <!-- Delete View Confirmation Modal -->
    <Teleport to="body">
      <Transition
        enter-active-class="transition-all duration-200 ease-out"
        enter-from-class="opacity-0"
        enter-to-class="opacity-100"
        leave-active-class="transition-all duration-150 ease-in"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
      >
        <div
          v-if="showDeleteView"
          class="fixed inset-0 z-50 flex items-center justify-center p-4"
          @click.self="showDeleteView = false"
        >
          <div class="absolute inset-0 bg-black/50"></div>
          <div class="relative bg-white dark:bg-slate-800 rounded-2xl shadow-2xl max-w-sm w-full p-6 border border-white/20 dark:border-slate-700/50">
            <div class="flex items-center gap-3 mb-4">
              <div class="w-10 h-10 rounded-xl bg-red-100 dark:bg-red-900/40 flex items-center justify-center">
                <Trash2 class="w-5 h-5 text-red-600 dark:text-red-400" />
              </div>
              <h3 class="text-lg font-bold text-slate-900 dark:text-slate-100">Ansicht löschen?</h3>
            </div>
            <p class="text-slate-600 dark:text-slate-400 mb-6 text-sm leading-relaxed">
              Möchtest du die Ansicht <strong class="text-slate-900 dark:text-slate-100">"{{ activeView?.view_name }}"</strong>
              mit {{ groups.length }} Gruppen und {{ totalContacts }} Kontakten unwiderruflich löschen?
              Alle Freigaben werden ebenfalls entfernt.
            </p>
            <div class="flex gap-3 justify-end">
              <button
                @click="showDeleteView = false"
                class="px-4 py-2.5 text-sm font-semibold text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-xl transition-colors"
              >
                Abbrechen
              </button>
              <button
                @click="deleteView"
                class="px-4 py-2.5 text-sm font-semibold text-white bg-red-600 hover:bg-red-700 rounded-xl transition-all shadow-md hover:shadow-lg shadow-red-500/20"
              >
                Löschen
              </button>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>

    <!-- Share View Modal -->
    <Teleport to="body">
      <Transition
        enter-active-class="transition-all duration-200 ease-out"
        enter-from-class="opacity-0"
        enter-to-class="opacity-100"
        leave-active-class="transition-all duration-150 ease-in"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
      >
        <div
          v-if="showShareModal"
          class="fixed inset-0 z-50 flex items-start justify-center p-3 sm:p-4 pt-[5vh] sm:pt-[10vh]"
          @click.self="showShareModal = false"
        >
          <div class="absolute inset-0 bg-black/50"></div>
          <div class="relative bg-white dark:bg-slate-800 rounded-2xl shadow-2xl max-w-md w-full border border-white/20 dark:border-slate-700/50 overflow-hidden max-h-[90vh] flex flex-col">
            <!-- Header -->
            <div class="p-4 sm:p-5 border-b border-slate-100 dark:border-slate-700 flex-shrink-0">
              <div class="flex items-center gap-3 mb-4">
                <div class="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center shadow-lg shadow-blue-500/20">
                  <Share2 class="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 class="text-lg font-bold text-slate-900 dark:text-slate-100">Ansicht teilen</h3>
                  <p class="text-xs text-slate-500 dark:text-slate-400">"{{ activeView?.view_name }}"</p>
                </div>
              </div>

              <!-- Public sharing toggle -->
              <div class="flex items-center justify-between p-3 rounded-xl mb-3"
                :class="activeView?.is_public
                  ? 'bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200/60 dark:border-emerald-800/40'
                  : 'bg-slate-50 dark:bg-slate-900/40 border border-slate-200/60 dark:border-slate-700/40'"
              >
                <div class="flex items-center gap-2.5 min-w-0">
                  <Globe class="w-4.5 h-4.5 flex-shrink-0" :class="activeView?.is_public ? 'text-emerald-500' : 'text-slate-400'" />
                  <div class="min-w-0">
                    <p class="text-sm font-semibold" :class="activeView?.is_public ? 'text-emerald-700 dark:text-emerald-300' : 'text-slate-700 dark:text-slate-300'">
                      Mit allen teilen
                    </p>
                    <p class="text-[11px]" :class="activeView?.is_public ? 'text-emerald-500 dark:text-emerald-400' : 'text-slate-400'">
                      {{ activeView?.is_public ? 'Jeder kann diese Ansicht annehmen' : 'Nur ausgewählte Personen' }}
                    </p>
                  </div>
                </div>
                <button
                  @click="togglePublicSharing"
                  :disabled="isPublicToggling"
                  class="relative w-11 h-6 rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 flex-shrink-0"
                  :class="activeView?.is_public
                    ? 'bg-emerald-500 focus:ring-emerald-500/30'
                    : 'bg-slate-300 dark:bg-slate-600 focus:ring-slate-500/30'"
                >
                  <span
                    class="absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform duration-200"
                    :class="activeView?.is_public ? 'translate-x-5' : 'translate-x-0'"
                  />
                </button>
              </div>

              <!-- Search + Role selector -->
              <div class="flex flex-col sm:flex-row gap-2">
                <div class="relative flex-1">
                  <Search class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    v-model="shareSearch"
                    @input="debouncedShareSearch"
                    placeholder="Person suchen..."
                    class="w-full pl-10 pr-4 py-2.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-600 rounded-xl focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 text-slate-800 dark:text-slate-100 text-sm outline-none transition-all"
                    autofocus
                  />
                </div>
                <select
                  v-model="shareRole"
                  class="px-3 py-2.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-600 rounded-xl text-sm text-slate-700 dark:text-slate-300 outline-none focus:ring-2 focus:ring-blue-500/30"
                >
                  <option value="reader">Leser</option>
                  <option value="editor">Editor</option>
                </select>
              </div>

              <!-- Search results -->
              <div v-if="shareSearch.length >= 2" class="mt-2 max-h-[30vh] overflow-y-auto">
                <div v-if="shareSearchLoading" class="flex items-center justify-center py-4">
                  <Loader2 class="w-4 h-4 text-blue-500 animate-spin" />
                </div>
                <div v-else-if="shareSearchResults.length === 0" class="py-4 text-center text-xs text-slate-400">
                  Keine Benutzer gefunden
                </div>
                <button
                  v-for="c in shareSearchResults"
                  :key="c.email"
                  @click="shareViewWith(c.email)"
                  class="w-full flex items-center gap-3 p-2.5 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors text-left"
                >
                  <div class="w-8 h-8 rounded-lg flex items-center justify-center" :class="avatarGradients[getInitials(c.display_name).charCodeAt(0) % avatarGradients.length]">
                    <span class="text-xs font-bold text-white">{{ getInitials(c.display_name) }}</span>
                  </div>
                  <div class="flex-1 min-w-0">
                    <div class="text-sm font-semibold text-slate-800 dark:text-slate-200 truncate">{{ c.display_name }}</div>
                    <div class="text-[11px] text-slate-400 truncate">{{ c.email }}</div>
                  </div>
                  <Plus class="w-4 h-4 text-blue-500" />
                </button>
              </div>
            </div>

            <!-- Current shares -->
            <div class="p-3 sm:p-4 overflow-y-auto flex-1 min-h-0">
              <p class="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-2">Aktuelle Freigaben</p>

              <div v-if="viewSharesLoading" class="flex items-center justify-center py-6">
                <Loader2 class="w-4 h-4 text-blue-500 animate-spin" />
              </div>
              <div v-else-if="viewShares.length === 0" class="py-6 text-center text-xs text-slate-400">
                Noch mit niemandem geteilt
              </div>
              <div v-else class="space-y-1.5">
                <div
                  v-for="share in viewShares"
                  :key="share.id"
                  class="flex items-center gap-3 p-2.5 rounded-lg bg-slate-50/80 dark:bg-slate-900/40"
                >
                  <div class="w-8 h-8 rounded-lg flex items-center justify-center" :class="avatarGradients[getInitials(share.shared_with_name || '?').charCodeAt(0) % avatarGradients.length]">
                    <span class="text-xs font-bold text-white">{{ getInitials(share.shared_with_name || '?') }}</span>
                  </div>
                  <div class="flex-1 min-w-0">
                    <div class="text-sm font-medium text-slate-800 dark:text-slate-200 truncate">{{ share.shared_with_name }}</div>
                    <div class="text-[11px] text-slate-400 truncate">{{ share.shared_with_email }}</div>
                  </div>
                  <span class="px-2 py-0.5 text-[10px] font-bold rounded-md" :class="{
                    'bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400': share.role === 'editor',
                    'bg-amber-100 dark:bg-amber-900/40 text-amber-600 dark:text-amber-400': share.role === 'reader'
                  }">
                    {{ share.role === 'editor' ? 'Editor' : 'Leser' }}
                  </span>
                  <span class="px-1.5 py-0.5 text-[9px] font-semibold rounded" :class="{
                    'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400': share.status === 'accepted',
                    'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-600 dark:text-yellow-400': share.status === 'pending',
                    'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400': share.status === 'declined'
                  }">
                    {{ share.status === 'accepted' ? 'Aktiv' : share.status === 'pending' ? 'Ausstehend' : 'Abgelehnt' }}
                  </span>
                  <button
                    @click="removeShare(share.id)"
                    class="p-1 text-slate-400 hover:text-red-500 rounded-md transition-colors"
                    title="Freigabe entfernen"
                  >
                    <X class="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </div>

            <!-- Footer -->
            <div class="p-3 sm:p-4 border-t border-slate-100 dark:border-slate-700 flex justify-end flex-shrink-0">
              <button
                @click="showShareModal = false"
                class="px-4 py-2 text-sm font-semibold text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-xl transition-colors"
              >
                Schließen
              </button>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, nextTick } from 'vue';
import {
  Plus, Loader2, Search, X, Trash2, ChevronDown, ChevronUp,
  UserPlus, Phone, PhoneCall, Video, MessageSquare,
  LayoutGrid, Users, Headphones, ExternalLink, Smartphone, Pencil,
  Share2, Star, Lock, Edit3, Mail, Copy, GripVertical, BarChart3, Heart, Globe, Palette,
  BellOff, Bell, Coffee, UtensilsCrossed, Monitor, Minimize2
} from 'lucide-vue-next';
import { getPresence, getPresenceClass, getPresenceLabel, fetchContactPhoto, presence, callRecords, callRecordsLoading, fetchCallHistory, userCallQueues, fetchUserCallQueues, socket } from '../websocketService';
import { authenticatedFetch } from '../oauth2-config';
import { getCallPreference, setCallPreference, removeCallPreference } from '../localStorageService';
import { makePhoneCall, callMode, setCallMode } from '../callService';
import CallCard from './CallCard.vue';

const API_URL = process.env.VUE_APP_API_URL || '__VUE_APP_API_URL__';

const props = defineProps({
  monitorMode: { type: Boolean, default: false }
});

const emit = defineEmits(['open-contact', 'open-notes', 'switch-tab', 'toggle-monitor-mode']);

// Constants
const groupColors = [
  'bg-blue-500', 'bg-violet-500', 'bg-emerald-500', 'bg-orange-500',
  'bg-pink-500', 'bg-cyan-500', 'bg-amber-500', 'bg-rose-500',
];

const avatarGradients = [
  'bg-gradient-to-br from-blue-500 to-blue-700',
  'bg-gradient-to-br from-violet-500 to-violet-700',
  'bg-gradient-to-br from-emerald-500 to-emerald-700',
  'bg-gradient-to-br from-orange-500 to-orange-700',
  'bg-gradient-to-br from-pink-500 to-pink-700',
  'bg-gradient-to-br from-cyan-500 to-cyan-700',
  'bg-gradient-to-br from-amber-500 to-amber-700',
  'bg-gradient-to-br from-rose-500 to-rose-700',
  'bg-gradient-to-br from-indigo-500 to-indigo-700',
  'bg-gradient-to-br from-teal-500 to-teal-700',
];

// State
const groups = ref([]);
const loading = ref(true);
const showAddGroup = ref(false);
const newGroupName = ref('');
const editingGroupId = ref(null);
const editGroupName = ref('');
const editInputRefs = ref(null);
const colorPickerGroupId = ref(null);

const contactPhotos = ref({});

const showAddContact = ref(false);
const addContactGroupId = ref(null);
const contactSearch = ref('');
const contactSearchResults = ref([]);
const contactSearchLoading = ref(false);
let contactSearchTimeout = null;

// Contact source filter (for add-contact search modal)
const availableSources = ref([]);
const sourcesLoaded = ref(false);
const contactSourceFilter = ref([]); // empty array = all selected

const showDeleteGroup = ref(false);
const deleteGroupTarget = ref(null);

const draggedContact = ref(null);
const draggedFromGroupId = ref(null);
const dragOverGroupId = ref(null);

// Drag type tracking ('contact' or 'group')
const dragType = ref(null);

// Group drag & drop
const draggedGroupId = ref(null);
const dragOverGroupIndex = ref(null);

// Contact within-group reorder
const dragOverContactIdx = ref(null);
const dragOverContactGroupId = ref(null);
const contactInsertBefore = ref(true);

// Stats toggle (persisted in localStorage)
const showDashboardStats = ref((() => {
  try {
    const val = localStorage.getItem(`${process.env.VUE_APP_STORAGE_PREFIX || 'primedirectory'}_show_stats`);
    return val === 'true';
  } catch { return false; }
})());

// Calls section position toggle (above/below groups)
const callsFirst = ref((() => {
  try {
    return localStorage.getItem(`${process.env.VUE_APP_STORAGE_PREFIX || 'primedirectory'}_calls_first`) === 'true';
  } catch { return false; }
})());

const toggleCallsFirst = () => {
  callsFirst.value = !callsFirst.value;
  try {
    localStorage.setItem(`${process.env.VUE_APP_STORAGE_PREFIX || 'primedirectory'}_calls_first`, callsFirst.value.toString());
  } catch {}
};

// Call menu state (teleported dropdown)
const activeCallMenu = ref(null);
const activeCallMenuContact = ref(null);
const callMenuPosition = ref({});
const callBtnRefs = ref({});

// Recent Calls state
const currentUserId = ref(localStorage.getItem('user_id') || '');

// DND / Pause dropdown state
const isSettingPresence = ref(false);
const showDndMenu = ref(false);
const dndMenuRef = ref(null);
const activeDndPreset = ref(null); // 'coffee' | 'lunch' | 'dnd' | null

const myPresenceAvailability = computed(() => {
  if (!currentUserId.value) return null;
  const p = getPresence(currentUserId.value);
  return p?.availability || null;
});

const isDnd = computed(() => {
  return activeDndPreset.value !== null || myPresenceAvailability.value === 'DoNotDisturb';
});

const activeDndLabel = computed(() => {
  const labels = { coffee: 'Kurze Pause (5 Min)', lunch: 'Mittagspause (30 Min)', dnd: 'Nicht stören (8 Std)' };
  return labels[activeDndPreset.value] || 'Nicht stören';
});

const setDndPreset = async (key, durationMinutes) => {
  if (isSettingPresence.value || !currentUserId.value) return;
  showDndMenu.value = false;
  isSettingPresence.value = true;
  try {
    const res = await authenticatedFetch(`${API_URL}/api/presence/status`, {
      method: 'PUT',
      body: JSON.stringify({ availability: 'DoNotDisturb', duration: durationMinutes })
    });
    if (res.ok) {
      activeDndPreset.value = key;
    }
  } catch (err) {
    console.error('Failed to set DND:', err);
  } finally {
    isSettingPresence.value = false;
  }
};

const clearDnd = async () => {
  if (isSettingPresence.value || !currentUserId.value) return;
  showDndMenu.value = false;
  isSettingPresence.value = true;
  try {
    const res = await authenticatedFetch(`${API_URL}/api/presence/status`, { method: 'DELETE' });
    if (res.ok) {
      activeDndPreset.value = null;
    }
  } catch (err) {
    console.error('Failed to clear DND:', err);
  } finally {
    isSettingPresence.value = false;
  }
};

const closeDndMenu = (event) => {
  if (dndMenuRef.value && !dndMenuRef.value.contains(event.target)) {
    showDndMenu.value = false;
  }
};

const dashboardCallTab = ref('personal');
const dashboardCallRecords = ref([]);
const dashboardCallsLoading = ref(false);
const dashboardCallQueues = ref([]);

// View state (multi-view + sharing)
const views = ref([]);
const activeViewId = ref(null);
const pendingInvitations = ref([]);
const showViewSelector = ref(false);
const viewSelectorRef = ref(null);
const showCreateView = ref(false);
const newViewName = ref('');
const editingViewName = ref(false);
const editViewNameValue = ref('');
const showDeleteView = ref(false);
const showShareModal = ref(false);
const shareSearch = ref('');
const shareSearchResults = ref([]);
const shareSearchLoading = ref(false);
const shareRole = ref('reader');
const viewShares = ref([]);
const viewSharesLoading = ref(false);
const isPublicToggling = ref(false);
let shareSearchTimeout = null;

// Computed - Views
const activeView = computed(() => views.value.find(v => v.id === activeViewId.value));
const viewRole = computed(() => activeView.value?.role || 'owner');
const canEdit = computed(() => viewRole.value === 'owner' || viewRole.value === 'editor');
const isViewOwner = computed(() => viewRole.value === 'owner');
const ownViews = computed(() => views.value.filter(v => v.role === 'owner'));
const sharedViews = computed(() => views.value.filter(v => v.role !== 'owner'));

// Computed
const totalContacts = computed(() => {
  return groups.value.reduce((sum, g) => sum + (g.contacts?.length || 0), 0);
});

const inCallCount = computed(() => {
  let count = 0;
  for (const group of groups.value) {
    for (const c of (group.contacts || [])) {
      if (isInCall(c.entra_id)) count++;
    }
  }
  return count;
});

const presenceStats = computed(() => {
  const stats = { available: 0, busy: 0, away: 0, offline: 0 };
  const seen = new Set();
  for (const group of groups.value) {
    for (const c of (group.contacts || [])) {
      if (!c.entra_id || seen.has(c.entra_id)) continue;
      seen.add(c.entra_id);
      const p = getPresence(c.entra_id);
      if (!p) { stats.offline++; continue; }
      switch (p.availability) {
        case 'Available': stats.available++; break;
        case 'Busy':
        case 'DoNotDisturb': stats.busy++; break;
        case 'Away':
        case 'BeRightBack': stats.away++; break;
        default: stats.offline++;
      }
    }
  }
  return stats;
});

// Quick Actions
const openCallQueueSettings = () => {
  window.open('https://dialin.teams.microsoft.com/usp/callqueues', '_blank', 'noopener,noreferrer');
};

// Group Presence Summary
const getGroupPresenceSummary = (group) => {
  const results = [];
  const counts = { available: 0, busy: 0, away: 0, offline: 0 };
  for (const c of (group.contacts || [])) {
    if (!c.entra_id) { counts.offline++; continue; }
    const p = getPresence(c.entra_id);
    if (!p) { counts.offline++; continue; }
    switch (p.availability) {
      case 'Available': counts.available++; break;
      case 'Busy':
      case 'DoNotDisturb': counts.busy++; break;
      case 'Away':
      case 'BeRightBack': counts.away++; break;
      default: counts.offline++;
    }
  }
  if (counts.available > 0) results.push({ class: 'bg-emerald-500', count: counts.available, label: 'Verfügbar' });
  if (counts.busy > 0) results.push({ class: 'bg-red-500', count: counts.busy, label: 'Beschäftigt' });
  if (counts.away > 0) results.push({ class: 'bg-amber-500', count: counts.away, label: 'Abwesend' });
  if (counts.offline > 0) results.push({ class: 'bg-slate-400', count: counts.offline, label: 'Offline' });
  return results;
};

// Load Dashboard (with retry for race conditions on first load)
const loadDashboard = async (retryCount = 0) => {
  try {
    loading.value = true;
    const viewParam = activeViewId.value ? `?view_id=${activeViewId.value}` : '';
    const response = await authenticatedFetch(`${API_URL}/api/dashboard${viewParam}`);
    if (response.ok) {
      groups.value = await response.json();
      loading.value = false;
      loadAllPhotos();
    } else if (response.status === 401 && retryCount < 2) {
      // Auth not ready yet — retry after a short delay (keep loading spinner)
      console.warn(`Dashboard load got 401, retrying (${retryCount + 1}/2)...`);
      setTimeout(() => loadDashboard(retryCount + 1), 1500);
    } else {
      loading.value = false;
    }
  } catch (error) {
    console.error('Error loading dashboard:', error);
    if (retryCount < 2) {
      // Network or auth error — retry after delay (keep loading spinner)
      console.warn(`Dashboard load failed, retrying (${retryCount + 1}/2)...`);
      setTimeout(() => loadDashboard(retryCount + 1), 1500);
    } else {
      loading.value = false;
    }
  }
};

const loadAllPhotos = async () => {
  for (const group of groups.value) {
    for (const contact of (group.contacts || [])) {
      if (contact.is_entra_user && contact.entra_id && contact.contact_email && !contactPhotos.value[contact.contact_email]) {
        fetchContactPhoto(contact.contact_email, contact.entra_id).then(url => {
          if (url) {
            contactPhotos.value[contact.contact_email] = url;
          }
        });
      }
    }
  }
};

// CRUD
const createGroup = async () => {
  if (!newGroupName.value.trim()) return;
  try {
    const response = await authenticatedFetch(`${API_URL}/api/dashboard/groups`, {
      method: 'POST',
      body: JSON.stringify({ group_name: newGroupName.value.trim(), view_id: activeViewId.value })
    });
    if (response.ok) {
      const group = await response.json();
      groups.value.push(group);
      newGroupName.value = '';
      showAddGroup.value = false;
    }
  } catch (error) {
    console.error('Error creating group:', error);
  }
};

const startEdit = (group) => {
  editingGroupId.value = group.id;
  editGroupName.value = group.group_name;
  nextTick(() => {
    if (editInputRefs.value) {
      const input = Array.isArray(editInputRefs.value) ? editInputRefs.value[0] : editInputRefs.value;
      input?.focus();
    }
  });
};

const saveGroupName = async (groupId) => {
  if (!editGroupName.value.trim()) {
    cancelEdit();
    return;
  }
  try {
    const response = await authenticatedFetch(`${API_URL}/api/dashboard/groups/${groupId}`, {
      method: 'PUT',
      body: JSON.stringify({ group_name: editGroupName.value.trim() })
    });
    if (response.ok) {
      const group = groups.value.find(g => g.id === groupId);
      if (group) group.group_name = editGroupName.value.trim();
    }
  } catch (error) {
    console.error('Error updating group:', error);
  }
  editingGroupId.value = null;
};

const cancelEdit = () => {
  editingGroupId.value = null;
};

const toggleColorPicker = (groupId) => {
  colorPickerGroupId.value = colorPickerGroupId.value === groupId ? null : groupId;
};

const setGroupColor = async (group, color) => {
  colorPickerGroupId.value = null;
  group.color = color;
  try {
    await authenticatedFetch(`${API_URL}/api/dashboard/groups/${group.id}`, {
      method: 'PUT',
      body: JSON.stringify({ color })
    });
  } catch (error) {
    console.error('Error updating group color:', error);
  }
};

const toggleCollapse = async (group) => {
  const newState = !group.is_collapsed;
  group.is_collapsed = newState;
  try {
    await authenticatedFetch(`${API_URL}/api/dashboard/groups/${group.id}`, {
      method: 'PUT',
      body: JSON.stringify({ is_collapsed: newState })
    });
  } catch (error) {
    console.error('Error toggling collapse:', error);
  }
};

const askDeleteGroup = (group) => {
  deleteGroupTarget.value = group;
  showDeleteGroup.value = true;
};

const confirmDeleteGroup = async () => {
  if (!deleteGroupTarget.value) return;
  try {
    const response = await authenticatedFetch(`${API_URL}/api/dashboard/groups/${deleteGroupTarget.value.id}`, {
      method: 'DELETE'
    });
    if (response.ok) {
      groups.value = groups.value.filter(g => g.id !== deleteGroupTarget.value.id);
    }
  } catch (error) {
    console.error('Error deleting group:', error);
  }
  showDeleteGroup.value = false;
  deleteGroupTarget.value = null;
};

// Add Contact
const openAddContact = async (groupId) => {
  addContactGroupId.value = groupId;
  contactSearch.value = '';
  contactSearchResults.value = [];
  showAddContact.value = true;

  // Load available sources once
  if (!sourcesLoaded.value) {
    try {
      const resp = await authenticatedFetch(`${API_URL}/api/contacts/filters`);
      if (resp.ok) {
        const data = await resp.json();
        availableSources.value = data.sources || [];
        contactSourceFilter.value = availableSources.value.map(s => s.id);
        sourcesLoaded.value = true;
      }
    } catch (e) { /* silent */ }
  }
};

const closeAddContact = () => {
  showAddContact.value = false;
  addContactGroupId.value = null;
};

const toggleSourceFilter = (sourceId) => {
  const idx = contactSourceFilter.value.indexOf(sourceId);
  if (idx === -1) {
    contactSourceFilter.value.push(sourceId);
  } else {
    // Prevent deselecting the last remaining source
    if (contactSourceFilter.value.length > 1) {
      contactSourceFilter.value.splice(idx, 1);
    }
  }
  if (contactSearch.value.length >= 2) searchContacts();
};

const debouncedContactSearch = () => {
  clearTimeout(contactSearchTimeout);
  contactSearchTimeout = setTimeout(() => searchContacts(), 300);
};

const searchContacts = async () => {
  if (contactSearch.value.length < 2) {
    contactSearchResults.value = [];
    return;
  }
  try {
    contactSearchLoading.value = true;
    let url = `${API_URL}/api/contacts?search=${encodeURIComponent(contactSearch.value)}&limit=20`;
    // Apply source filter only when a subset is selected
    if (availableSources.value.length > 0 && contactSourceFilter.value.length < availableSources.value.length) {
      url += `&source=${contactSourceFilter.value.join(',')}`;
    }
    const response = await authenticatedFetch(url);
    if (response.ok) {
      const data = await response.json();
      contactSearchResults.value = data.contacts || [];
    }
  } catch (error) {
    console.error('Error searching contacts:', error);
  } finally {
    contactSearchLoading.value = false;
  }
};

const addContactToGroup = async (email) => {
  if (!addContactGroupId.value || isContactInGroup(email, addContactGroupId.value)) return;
  try {
    const response = await authenticatedFetch(`${API_URL}/api/dashboard/groups/${addContactGroupId.value}/contacts`, {
      method: 'POST',
      body: JSON.stringify({ contact_email: email })
    });
    if (response.ok) {
      const contact = await response.json();
      const group = groups.value.find(g => g.id === addContactGroupId.value);
      if (group) {
        if (!group.contacts) group.contacts = [];
        group.contacts.push(contact);
        if (contact.is_entra_user && contact.entra_id && contact.contact_email) {
          fetchContactPhoto(contact.contact_email, contact.entra_id).then(url => {
            if (url) contactPhotos.value[contact.contact_email] = url;
          });
        }
      }
    }
  } catch (error) {
    console.error('Error adding contact:', error);
  }
};

const isContactInGroup = (email, groupId) => {
  const group = groups.value.find(g => g.id === groupId);
  return group?.contacts?.some(c => c.contact_email === email) || false;
};

const removeContact = async (groupId, email) => {
  try {
    const response = await authenticatedFetch(
      `${API_URL}/api/dashboard/groups/${groupId}/contacts/${encodeURIComponent(email)}`,
      { method: 'DELETE' }
    );
    if (response.ok) {
      const group = groups.value.find(g => g.id === groupId);
      if (group) {
        group.contacts = group.contacts.filter(c => c.contact_email !== email);
      }
    }
  } catch (error) {
    console.error('Error removing contact:', error);
  }
};

// Drag & Drop (contacts between groups + within group reorder)
const onContactDragStart = (event, contact, groupId) => {
  dragType.value = 'contact';
  draggedContact.value = contact;
  draggedFromGroupId.value = groupId;
  event.dataTransfer.effectAllowed = 'move';
  event.dataTransfer.setData('text/plain', contact.contact_email);
  event.target.style.opacity = '0.4';
};

const onContactDragEnd = (event) => {
  event.target.style.opacity = '1';
  dragType.value = null;
  draggedContact.value = null;
  draggedFromGroupId.value = null;
  dragOverGroupId.value = null;
  dragOverContactIdx.value = null;
  dragOverContactGroupId.value = null;
};

// Contact drag over another contact (within-group reorder)
const onContactDragOver = (event, groupId, contactIndex) => {
  if (dragType.value !== 'contact' || !draggedContact.value) return;
  event.dataTransfer.dropEffect = 'move';
  dragOverContactGroupId.value = groupId;
  dragOverContactIdx.value = contactIndex;
};

const onContactDragLeave = (groupId, contactIndex) => {
  if (dragOverContactGroupId.value === groupId && dragOverContactIdx.value === contactIndex) {
    dragOverContactIdx.value = null;
    dragOverContactGroupId.value = null;
  }
};

// Drop on a specific contact (within-group reorder or cross-group insert at position)
const onContactDrop = async (event, targetGroupId, targetIndex) => {
  event.stopPropagation();
  dragOverContactIdx.value = null;
  dragOverContactGroupId.value = null;
  dragOverGroupId.value = null;

  if (!draggedContact.value || !draggedFromGroupId.value) return;

  const contact = { ...draggedContact.value };
  const fromGroupId = draggedFromGroupId.value;

  if (fromGroupId === targetGroupId) {
    // Within-group reorder
    const group = groups.value.find(g => g.id === targetGroupId);
    if (!group || !group.contacts) return;

    const currentIndex = group.contacts.findIndex(c => c.contact_email === contact.contact_email);
    if (currentIndex === -1 || currentIndex === targetIndex) return;

    // Reorder array
    const newContacts = [...group.contacts];
    newContacts.splice(currentIndex, 1);
    newContacts.splice(targetIndex > currentIndex ? targetIndex - 1 : targetIndex, 0, contact);
    group.contacts = newContacts;

    // Send reorder to backend
    try {
      await authenticatedFetch(`${API_URL}/api/dashboard/contacts/reorder`, {
        method: 'PUT',
        body: JSON.stringify({
          group_id: targetGroupId,
          contact_emails: newContacts.map(c => c.contact_email)
        })
      });
    } catch (error) {
      console.error('Error reordering contacts:', error);
      await loadDashboard();
    }
  } else {
    // Cross-group move, insert at target position
    const fromGroup = groups.value.find(g => g.id === fromGroupId);
    const toGroup = groups.value.find(g => g.id === targetGroupId);

    if (fromGroup && toGroup) {
      fromGroup.contacts = fromGroup.contacts.filter(c => c.contact_email !== contact.contact_email);
      if (!toGroup.contacts) toGroup.contacts = [];
      toGroup.contacts.splice(targetIndex, 0, contact);
    }

    try {
      await authenticatedFetch(`${API_URL}/api/dashboard/contacts/move`, {
        method: 'PUT',
        body: JSON.stringify({
          contact_email: contact.contact_email,
          from_group_id: fromGroupId,
          to_group_id: targetGroupId,
          new_sort_order: targetIndex
        })
      });
      // Also send full reorder to ensure consistency
      if (toGroup) {
        await authenticatedFetch(`${API_URL}/api/dashboard/contacts/reorder`, {
          method: 'PUT',
          body: JSON.stringify({
            group_id: targetGroupId,
            contact_emails: toGroup.contacts.map(c => c.contact_email)
          })
        });
      }
    } catch (error) {
      console.error('Error moving contact:', error);
      await loadDashboard();
    }
  }
};

const onDragOver = (event, groupId, groupIndex) => {
  if (dragType.value === 'contact' && draggedContact.value) {
    dragOverGroupId.value = groupId;
    event.dataTransfer.dropEffect = 'move';
  } else if (dragType.value === 'group' && draggedGroupId.value && draggedGroupId.value !== groupId) {
    dragOverGroupIndex.value = groupIndex;
    event.dataTransfer.dropEffect = 'move';
  }
};

const onDragLeave = (groupId, groupIndex) => {
  if (dragOverGroupId.value === groupId) {
    dragOverGroupId.value = null;
  }
  if (dragOverGroupIndex.value === groupIndex) {
    dragOverGroupIndex.value = null;
  }
};

const onDrop = async (event, targetGroupId, targetGroupIndex) => {
  // Handle group drop
  if (dragType.value === 'group' && draggedGroupId.value) {
    dragOverGroupIndex.value = null;
    const fromIndex = groups.value.findIndex(g => g.id === draggedGroupId.value);
    if (fromIndex === -1 || fromIndex === targetGroupIndex) return;

    const movedGroup = groups.value.splice(fromIndex, 1)[0];
    const insertAt = targetGroupIndex > fromIndex ? targetGroupIndex - 1 : targetGroupIndex;
    groups.value.splice(insertAt, 0, movedGroup);

    // Send reorder to backend
    if (!activeViewId.value) {
      console.warn('Cannot reorder: no active view');
      return;
    }
    try {
      await authenticatedFetch(`${API_URL}/api/dashboard/groups/reorder`, {
        method: 'PUT',
        body: JSON.stringify({
          view_id: activeViewId.value,
          group_ids: groups.value.map(g => g.id)
        })
      });
    } catch (error) {
      console.error('Error reordering groups:', error);
      await loadDashboard();
    }
    return;
  }

  // Handle contact drop on group container (append to end)
  dragOverGroupId.value = null;
  if (!draggedContact.value || !draggedFromGroupId.value) return;
  if (draggedFromGroupId.value === targetGroupId) return;

  const contact = { ...draggedContact.value };
  const fromGroupId = draggedFromGroupId.value;

  const fromGroup = groups.value.find(g => g.id === fromGroupId);
  const toGroup = groups.value.find(g => g.id === targetGroupId);

  if (fromGroup && toGroup) {
    fromGroup.contacts = fromGroup.contacts.filter(c => c.contact_email !== contact.contact_email);
    if (!toGroup.contacts) toGroup.contacts = [];
    toGroup.contacts.push(contact);
  }

  try {
    await authenticatedFetch(`${API_URL}/api/dashboard/contacts/move`, {
      method: 'PUT',
      body: JSON.stringify({
        contact_email: contact.contact_email,
        from_group_id: fromGroupId,
        to_group_id: targetGroupId,
        new_sort_order: (toGroup?.contacts?.length || 1) - 1
      })
    });
  } catch (error) {
    console.error('Error moving contact:', error);
    await loadDashboard();
  }
};

// Group Drag & Drop
const onGroupDragStart = (event, group) => {
  dragType.value = 'group';
  draggedGroupId.value = group.id;
  event.dataTransfer.effectAllowed = 'move';
  event.dataTransfer.setData('text/plain', `group-${group.id}`);
};

const onGroupDragEnd = () => {
  dragType.value = null;
  draggedGroupId.value = null;
  dragOverGroupIndex.value = null;
};

// Stats toggle
const toggleDashboardStats = () => {
  showDashboardStats.value = !showDashboardStats.value;
  try {
    localStorage.setItem(`${process.env.VUE_APP_STORAGE_PREFIX || 'primedirectory'}_show_stats`, showDashboardStats.value);
  } catch {}
};

// Presence Helpers
const isInCall = (entraId) => {
  if (!entraId) return false;
  const p = getPresence(entraId);
  return p?.activity === 'InACall' || p?.activity === 'InAConferenceCall';
};

const isInMeeting = (entraId) => {
  if (!entraId) return false;
  const p = getPresence(entraId);
  return p?.activity === 'InAMeeting' || p?.activity === 'Presenting';
};

// Utility
const getInitials = (name) => {
  if (!name) return '?';
  return name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
};

const hasValidEmail = (contact) => {
  return contact.contact_email && !contact.contact_email.endsWith('@mailbox-contact.local');
};

// Phone helpers for call dropdown
const getBusinessPhone = (contact) => {
  if (!contact) return null;
  const phones = contact.business_phones;
  if (!phones) return null;
  const phoneArray = typeof phones === 'string' ? JSON.parse(phones || '[]') : (phones || []);
  return phoneArray[0] || null;
};

const getMobilePhone = (contact) => {
  if (!contact) return null;
  return contact.mobile_phone || null;
};

const hasAnyCallOption = (contact) => {
  return hasValidEmail(contact) || !!getBusinessPhone(contact) || !!getMobilePhone(contact);
};

const hasMultipleCallOptions = (contact) => {
  let count = 0;
  if (hasValidEmail(contact)) count++;
  if (getBusinessPhone(contact)) count++;
  if (getMobilePhone(contact)) count++;
  return count > 1;
};

// Call menu with Teleport positioning
const toggleCallMenu = (event, contact) => {
  if (activeCallMenu.value === contact.contact_email) {
    closeCallMenu();
    return;
  }
  const btn = event.currentTarget;
  const rect = btn.getBoundingClientRect();
  const dropdownWidth = 200;
  let left = rect.right - dropdownWidth;
  let top = rect.bottom + 6;
  if (left < 8) left = 8;
  if (top + 200 > window.innerHeight) {
    top = rect.top - 6;
    callMenuPosition.value = { left: left + 'px', bottom: (window.innerHeight - top) + 'px' };
  } else {
    callMenuPosition.value = { left: left + 'px', top: top + 'px' };
  }
  activeCallMenu.value = contact.contact_email;
  activeCallMenuContact.value = contact;
};

const callNumber = (number) => {
  makePhoneCall(number);
};

const openTeamsChat = (contact) => {
  window.open(
    `https://teams.microsoft.com/l/chat/0/0?users=${encodeURIComponent(contact.contact_email)}`,
    '_blank'
  );
};

const openTeamsCall = (contact) => {
  window.open(
    `https://teams.microsoft.com/l/call/0/0?users=${encodeURIComponent(contact.contact_email)}`,
    '_blank'
  );
};

// Close call menu on outside click
const closeCallMenu = () => {
  activeCallMenu.value = null;
  activeCallMenuContact.value = null;
};

// Favorite call method — direct call on button click
const handleCallClick = (event, contact) => {
  const pref = getCallPreference(contact.contact_email);

  if (pref) {
    // Check if preferred method is still available
    if (pref === 'teams' && hasValidEmail(contact)) {
      openTeamsCall(contact);
      return;
    }
    if (pref === 'business' && getBusinessPhone(contact)) {
      callNumber(getBusinessPhone(contact));
      return;
    }
    if (pref === 'mobile' && getMobilePhone(contact)) {
      callNumber(getMobilePhone(contact));
      return;
    }
    // Favorite no longer available — fall through to dropdown
  }

  // If only one option, call directly
  if (!hasMultipleCallOptions(contact)) {
    if (hasValidEmail(contact)) { openTeamsCall(contact); return; }
    if (getBusinessPhone(contact)) { callNumber(getBusinessPhone(contact)); return; }
    if (getMobilePhone(contact)) { callNumber(getMobilePhone(contact)); return; }
  }

  // Multiple options, no favorite — show dropdown
  toggleCallMenu(event, contact);
};

const getCallPrefLabel = (contact) => {
  const pref = getCallPreference(contact.contact_email);
  if (pref === 'teams') return 'Standard: Teams Anruf';
  if (pref === 'business') return `Standard: ${getBusinessPhone(contact)}`;
  if (pref === 'mobile') return `Standard: ${getMobilePhone(contact)}`;
  return 'Anrufen';
};

// Recent Calls
const loadDashboardCalls = async () => {
  dashboardCallsLoading.value = true;
  try {
    const options = { days: 7 };
    if (dashboardCallTab.value === 'personal') {
      options.personal = true;
    } else {
      options.queueEmail = dashboardCallTab.value;
    }
    await fetchCallHistory(options, true);
    dashboardCallRecords.value = callRecords.value.slice(0, 30);
  } catch (error) {
    console.error('Error loading dashboard calls:', error);
  } finally {
    dashboardCallsLoading.value = false;
  }
};

const loadDashboardCallQueues = async () => {
  try {
    await fetchUserCallQueues();
    dashboardCallQueues.value = userCallQueues.value || [];
  } catch (error) {
    console.error('Error loading call queues:', error);
  }
};

const switchDashboardCallTab = (tab) => {
  dashboardCallTab.value = tab;
  loadDashboardCalls();
};

// Real-time call record handler for dashboard
const onNewCallRecord = () => {
  // Silently reload dashboard calls when a new call record arrives
  loadDashboardCalls();
};

// ========================================
// MONITOR MODE — Quick Dial & Contact Actions
// ========================================

const quickDialInput = ref('');
const quickDialResults = ref([]);
const quickDialLoading = ref(false);
const showQuickDialResults = ref(false);
let quickDialTimeout = null;
const quickDialInputRef = ref(null);

// Monitor mode: mini call menu for contacts without a heart preference
const monitorCallMenu = ref(null); // contact_email of open menu
const monitorCallMenuContact = ref(null);
const monitorCallMenuPos = ref({});

// Monitor mode: Teleported dropdown state
const monitorViewSelectorRef = ref(null);
const monitorViewSelectorOpen = ref(false);
const monitorViewSelectorPos = ref({});
const monitorDndRef = ref(null);
const monitorDndOpen = ref(false);
const monitorDndPos = ref({});

const toggleMonitorViewSelector = () => {
  monitorDndOpen.value = false;
  monitorViewSelectorOpen.value = !monitorViewSelectorOpen.value;
  if (monitorViewSelectorOpen.value && monitorViewSelectorRef.value) {
    const rect = monitorViewSelectorRef.value.getBoundingClientRect();
    monitorViewSelectorPos.value = { left: rect.left + 'px', top: (rect.bottom + 4) + 'px' };
  }
};

const toggleMonitorDndMenu = () => {
  monitorViewSelectorOpen.value = false;
  monitorDndOpen.value = !monitorDndOpen.value;
  if (monitorDndOpen.value && monitorDndRef.value) {
    const rect = monitorDndRef.value.getBoundingClientRect();
    monitorDndPos.value = { right: (window.innerWidth - rect.right) + 'px', top: (rect.bottom + 4) + 'px' };
  }
};

const dialpadKeys = [
  ['1', '2', '3'],
  ['4', '5', '6'],
  ['7', '8', '9'],
  ['*', '0', '#']
];

const appendDialpad = (key) => {
  quickDialInput.value += key;
  quickDialInputRef.value?.focus();
};

const clearQuickDial = () => {
  quickDialInput.value = '';
  quickDialResults.value = [];
  showQuickDialResults.value = false;
  quickDialInputRef.value?.focus();
};

const onQuickDialInput = () => {
  clearTimeout(quickDialTimeout);
  const term = quickDialInput.value.trim();
  if (!term || term.length < 2) {
    quickDialResults.value = [];
    showQuickDialResults.value = false;
    return;
  }
  // Phone number → no search needed, user will just hit call
  const phonePattern = /^[\+\d\s\-\(\)\/\.]{4,}$/;
  if (phonePattern.test(term)) {
    quickDialResults.value = [];
    showQuickDialResults.value = false;
    return;
  }
  quickDialTimeout = setTimeout(() => searchQuickDial(term), 300);
};

const searchQuickDial = async (term) => {
  try {
    quickDialLoading.value = true;
    const response = await authenticatedFetch(
      `${API_URL}/api/contacts?search=${encodeURIComponent(term)}&limit=8`
    );
    if (response.ok) {
      const data = await response.json();
      quickDialResults.value = data.contacts || [];
      showQuickDialResults.value = quickDialResults.value.length > 0;
    }
  } catch (error) {
    console.error('Error searching quick dial:', error);
  } finally {
    quickDialLoading.value = false;
  }
};

const executeQuickDial = () => {
  const term = quickDialInput.value.trim();
  if (!term) return;
  // Phone number → call via Teams or native
  const phonePattern = /^[\+\d\s\-\(\)\/\.]{4,}$/;
  if (phonePattern.test(term)) {
    makePhoneCall(term);
    return;
  }
  // Single result → call directly
  if (quickDialResults.value.length === 1) {
    monitorQuickCall(quickDialResults.value[0]);
    return;
  }
};

// Handle contact click in monitor mode: call heart preference or show mini menu
const monitorContactClick = (event, contact) => {
  const pref = getCallPreference(contact.contact_email || contact.email);
  const email = contact.contact_email || contact.email;
  const c = { ...contact, contact_email: email };

  if (pref) {
    if (pref === 'teams' && hasValidEmail(c)) { openTeamsCall(c); return; }
    if (pref === 'business' && getBusinessPhone(c)) { callNumber(getBusinessPhone(c)); return; }
    if (pref === 'mobile' && getMobilePhone(c)) { callNumber(getMobilePhone(c)); return; }
  }
  // Only one option → call directly
  if (!hasMultipleCallOptions(c)) {
    if (hasValidEmail(c)) { openTeamsCall(c); return; }
    if (getBusinessPhone(c)) { callNumber(getBusinessPhone(c)); return; }
    if (getMobilePhone(c)) { callNumber(getMobilePhone(c)); return; }
    return;
  }
  // Multiple options, no heart → show mini dropdown
  openMonitorCallMenu(event, c);
};

// Handle contact click from quick dial results
const monitorQuickCall = (contact) => {
  const pref = getCallPreference(contact.contact_email || contact.email);
  const email = contact.contact_email || contact.email;
  const c = { ...contact, contact_email: email };

  if (pref) {
    if (pref === 'teams' && hasValidEmail(c)) { openTeamsCall(c); return; }
    if (pref === 'business' && getBusinessPhone(c)) { callNumber(getBusinessPhone(c)); return; }
    if (pref === 'mobile' && getMobilePhone(c)) { callNumber(getMobilePhone(c)); return; }
  }
  // Only one option → call directly
  if (!hasMultipleCallOptions(c)) {
    if (hasValidEmail(c)) { openTeamsCall(c); return; }
    if (getBusinessPhone(c)) { callNumber(getBusinessPhone(c)); return; }
    if (getMobilePhone(c)) { callNumber(getMobilePhone(c)); return; }
    return;
  }
  // Multiple options, no heart → show mini dropdown
  openMonitorCallMenu(null, c);
};

const openMonitorCallMenu = (event, contact) => {
  if (event) event.stopPropagation();
  const email = contact.contact_email || contact.email;
  if (monitorCallMenu.value === email) {
    closeMonitorCallMenu();
    return;
  }
  monitorCallMenu.value = email;
  monitorCallMenuContact.value = { ...contact, contact_email: email };
  // Position near click or center
  if (event) {
    const rect = event.currentTarget.getBoundingClientRect();
    monitorCallMenuPos.value = {
      left: Math.min(rect.left, window.innerWidth - 240) + 'px',
      top: (rect.bottom + 4) + 'px'
    };
  } else {
    monitorCallMenuPos.value = { left: '50%', top: '50%', transform: 'translate(-50%, -50%)' };
  }
};

const closeMonitorCallMenu = () => {
  monitorCallMenu.value = null;
  monitorCallMenuContact.value = null;
  monitorViewSelectorOpen.value = false;
  monitorDndOpen.value = false;
};

const monitorSetHeart = async (contact, method) => {
  const email = contact.contact_email || contact.email;
  const currentPref = getCallPreference(email);
  if (currentPref === method) {
    await removeCallPreference(email);
  } else {
    await setCallPreference(email, method);
  }
};

const monitorCallAndClose = (contact, method) => {
  const email = contact.contact_email || contact.email;
  const c = { ...contact, contact_email: email };
  if (method === 'teams') openTeamsCall(c);
  else if (method === 'business') callNumber(getBusinessPhone(c));
  else if (method === 'mobile') callNumber(getMobilePhone(c));
  closeMonitorCallMenu();
};

// All calls merged for monitor mode sidebar (personal + queue, sorted by time)
const monitorCallRecords = computed(() => {
  return dashboardCallRecords.value.slice(0, 25);
});

// Monitor mode call record helpers
const getMonitorCallName = (call) => {
  if (call.direction === 'incoming' || call.organizer_id !== currentUserId.value) {
    return call.external_contact_name || call.organizer_name || call.external_phone || 'Unbekannt';
  }
  // Outgoing
  const participants = call.participants || [];
  const callee = participants.find(p => p.user_id !== currentUserId.value);
  return call.external_contact_name || callee?.user_name || call.external_phone || 'Unbekannt';
};

const getMonitorCallIconClass = (call) => {
  if (call.is_missed) return 'bg-red-500';
  if (call.direction === 'incoming') return 'bg-blue-500';
  return 'bg-emerald-500';
};

const getMonitorCallTime = (call) => {
  if (!call.start_time) return '';
  const d = new Date(call.start_time);
  const now = new Date();
  const diff = now - d;
  if (diff < 60000) return 'gerade eben';
  if (diff < 3600000) return `vor ${Math.floor(diff / 60000)} Min`;
  if (diff < 86400000) return d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
  return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' }) + ' ' + d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
};

const getMonitorCallTitle = (call) => {
  const name = getMonitorCallName(call);
  const time = getMonitorCallTime(call);
  const dir = call.direction === 'incoming' ? 'Eingehend' : 'Ausgehend';
  return `${name} — ${dir} — ${time}`;
};

const formatDuration = (seconds) => {
  if (!seconds || seconds <= 0) return '';
  if (seconds < 60) return `${seconds}s`;
  const min = Math.floor(seconds / 60);
  const sec = seconds % 60;
  return sec > 0 ? `${min}:${String(sec).padStart(2, '0')}` : `${min} Min`;
};

const monitorCallbackFromRecord = (call) => {
  const phone = call.external_phone || call.organizer_phone;
  if (phone) {
    makePhoneCall(phone);
  } else if (call.organizer_email && call.direction === 'incoming') {
    window.open(`https://teams.microsoft.com/l/call/0/0?users=${encodeURIComponent(call.organizer_email)}`, '_blank');
  }
};

// ========================================
// VIEW MANAGEMENT
// ========================================

const loadViews = async () => {
  try {
    const response = await authenticatedFetch(`${API_URL}/api/dashboard/views`);
    if (response.ok) {
      views.value = await response.json();
      if (!activeViewId.value && views.value.length > 0) {
        const defaultView = views.value.find(v => v.is_default);
        activeViewId.value = defaultView?.id || views.value[0]?.id;
      }
    }
  } catch (error) {
    console.error('Error loading views:', error);
  }
};

const loadInvitations = async () => {
  try {
    const response = await authenticatedFetch(`${API_URL}/api/dashboard/invitations`);
    if (response.ok) {
      let invitations = await response.json();
      // Filter out dismissed public views
      const dismissedKey = `${process.env.VUE_APP_STORAGE_PREFIX || 'primedirectory'}_dismissed_public_views`;
      const dismissed = JSON.parse(localStorage.getItem(dismissedKey) || '[]');
      if (dismissed.length > 0) {
        invitations = invitations.filter(inv => !inv.is_public_invite || !dismissed.includes(inv.view_id));
      }
      pendingInvitations.value = invitations;
    }
  } catch (error) {
    console.error('Error loading invitations:', error);
  }
};

const switchView = (viewId) => {
  activeViewId.value = viewId;
  showViewSelector.value = false;
  loadDashboard();
};

const createView = async () => {
  if (!newViewName.value.trim()) return;
  try {
    const response = await authenticatedFetch(`${API_URL}/api/dashboard/views`, {
      method: 'POST',
      body: JSON.stringify({ view_name: newViewName.value.trim() })
    });
    if (response.ok) {
      const view = await response.json();
      views.value.push(view);
      activeViewId.value = view.id;
      newViewName.value = '';
      showCreateView.value = false;
      loadDashboard();
    }
  } catch (error) {
    console.error('Error creating view:', error);
  }
};

const startEditViewName = () => {
  if (!activeView.value) return;
  editViewNameValue.value = activeView.value.view_name;
  editingViewName.value = true;
};

const renameView = async () => {
  if (!editViewNameValue.value.trim() || !activeViewId.value) return;
  try {
    const response = await authenticatedFetch(`${API_URL}/api/dashboard/views/${activeViewId.value}`, {
      method: 'PUT',
      body: JSON.stringify({ view_name: editViewNameValue.value.trim() })
    });
    if (response.ok) {
      const view = views.value.find(v => v.id === activeViewId.value);
      if (view) view.view_name = editViewNameValue.value.trim();
      editingViewName.value = false;
    }
  } catch (error) {
    console.error('Error renaming view:', error);
  }
};

const deleteView = async () => {
  if (!activeViewId.value) return;
  try {
    const response = await authenticatedFetch(`${API_URL}/api/dashboard/views/${activeViewId.value}`, {
      method: 'DELETE'
    });
    if (response.ok) {
      views.value = views.value.filter(v => v.id !== activeViewId.value);
      activeViewId.value = views.value[0]?.id || null;
      showDeleteView.value = false;
      loadDashboard();
    }
  } catch (error) {
    console.error('Error deleting view:', error);
  }
};

const copyView = async (viewId) => {
  try {
    const response = await authenticatedFetch(`${API_URL}/api/dashboard/views/${viewId}/copy`, {
      method: 'POST',
      body: JSON.stringify({})
    });
    if (response.ok) {
      const newView = await response.json();
      views.value.push(newView);
      activeViewId.value = newView.id;
      showViewSelector.value = false;
      loadDashboard();
    }
  } catch (error) {
    console.error('Error copying view:', error);
  }
};

const setDefaultView = async () => {
  if (!activeViewId.value) return;
  try {
    const response = await authenticatedFetch(`${API_URL}/api/dashboard/views/${activeViewId.value}/default`, {
      method: 'PUT'
    });
    if (response.ok) {
      views.value.forEach(v => v.is_default = (v.id === activeViewId.value));
    }
  } catch (error) {
    console.error('Error setting default view:', error);
  }
};

// ========================================
// SHARING
// ========================================

const openShareModal = async () => {
  showShareModal.value = true;
  shareSearch.value = '';
  shareSearchResults.value = [];
  shareRole.value = 'reader';
  loadViewShares();
};

const togglePublicSharing = async () => {
  if (!activeViewId.value || !activeView.value) return;
  isPublicToggling.value = true;
  try {
    const newPublicState = !activeView.value.is_public;
    const response = await authenticatedFetch(`${API_URL}/api/dashboard/views/${activeViewId.value}/public`, {
      method: 'PUT',
      body: JSON.stringify({ is_public: newPublicState, public_role: shareRole.value })
    });
    if (response.ok) {
      const data = await response.json();
      activeView.value.is_public = data.is_public;
      activeView.value.public_role = data.public_role;
    }
  } catch (error) {
    console.error('Error toggling public sharing:', error);
  } finally {
    isPublicToggling.value = false;
  }
};

const loadViewShares = async () => {
  if (!activeViewId.value) return;
  viewSharesLoading.value = true;
  try {
    const response = await authenticatedFetch(`${API_URL}/api/dashboard/views/${activeViewId.value}/shares`);
    if (response.ok) {
      viewShares.value = await response.json();
    }
  } catch (error) {
    console.error('Error loading shares:', error);
  } finally {
    viewSharesLoading.value = false;
  }
};

const debouncedShareSearch = () => {
  clearTimeout(shareSearchTimeout);
  shareSearchTimeout = setTimeout(() => searchShareUsers(), 300);
};

const searchShareUsers = async () => {
  if (shareSearch.value.length < 2) {
    shareSearchResults.value = [];
    return;
  }
  try {
    shareSearchLoading.value = true;
    const response = await authenticatedFetch(
      `${API_URL}/api/contacts?search=${encodeURIComponent(shareSearch.value)}&limit=10`
    );
    if (response.ok) {
      const data = await response.json();
      // Only show Entra users not already shared
      shareSearchResults.value = (data.contacts || []).filter(c =>
        c.entra_id && !viewShares.value.some(s => s.shared_with_email === c.email)
      );
    }
  } catch (error) {
    console.error('Error searching users:', error);
  } finally {
    shareSearchLoading.value = false;
  }
};

const shareViewWith = async (email) => {
  if (!activeViewId.value) return;
  try {
    const response = await authenticatedFetch(`${API_URL}/api/dashboard/views/${activeViewId.value}/share`, {
      method: 'POST',
      body: JSON.stringify({ shared_with_email: email, role: shareRole.value })
    });
    if (response.ok) {
      const share = await response.json();
      viewShares.value.push(share);
      shareSearch.value = '';
      shareSearchResults.value = [];
    }
  } catch (error) {
    console.error('Error sharing view:', error);
  }
};

const removeShare = async (shareId) => {
  if (!activeViewId.value) return;
  try {
    const response = await authenticatedFetch(
      `${API_URL}/api/dashboard/views/${activeViewId.value}/shares/${shareId}`,
      { method: 'DELETE' }
    );
    if (response.ok) {
      viewShares.value = viewShares.value.filter(s => s.id !== shareId);
    }
  } catch (error) {
    console.error('Error removing share:', error);
  }
};

const acceptInvitation = async (inv) => {
  try {
    let response;
    if (inv.is_public_invite) {
      // Accept public view - creates a share record
      response = await authenticatedFetch(`${API_URL}/api/dashboard/views/${inv.view_id}/accept-public`, {
        method: 'PUT'
      });
    } else {
      // Accept direct invitation
      response = await authenticatedFetch(`${API_URL}/api/dashboard/shares/${inv.id}/accept`, {
        method: 'PUT'
      });
    }
    if (response.ok) {
      pendingInvitations.value = pendingInvitations.value.filter(i => i !== inv);
      await loadViews();
    }
  } catch (error) {
    console.error('Error accepting invitation:', error);
  }
};

const declineInvitation = async (inv) => {
  try {
    if (inv.is_public_invite) {
      // For public views: just dismiss locally (no server-side decline needed as there's no share record)
      pendingInvitations.value = pendingInvitations.value.filter(i => i !== inv);
      // Store dismissed public view IDs in localStorage to avoid showing again
      const dismissedKey = `${process.env.VUE_APP_STORAGE_PREFIX || 'primedirectory'}_dismissed_public_views`;
      const dismissed = JSON.parse(localStorage.getItem(dismissedKey) || '[]');
      if (!dismissed.includes(inv.view_id)) {
        dismissed.push(inv.view_id);
        localStorage.setItem(dismissedKey, JSON.stringify(dismissed));
      }
      return;
    }
    const response = await authenticatedFetch(`${API_URL}/api/dashboard/shares/${inv.id}/decline`, {
      method: 'PUT'
    });
    if (response.ok) {
      pendingInvitations.value = pendingInvitations.value.filter(i => i !== inv);
    }
  } catch (error) {
    console.error('Error declining invitation:', error);
  }
};

// Close view selector on outside click
const closeViewSelector = (event) => {
  if (viewSelectorRef.value && !viewSelectorRef.value.contains(event.target)) {
    showViewSelector.value = false;
  }
};

// Socket.IO real-time listeners
const onDashboardViewUpdated = (data) => {
  if (data.viewId === activeViewId.value) {
    loadDashboard();
  }
  loadViews();
};

const onDashboardInvitation = (data) => {
  pendingInvitations.value.unshift(data);
};

const onDashboardShareAccepted = (data) => {
  console.log(`${data.accepted_by_name} hat die Einladung für "${data.view_name}" angenommen`);
  loadViews();
};

// Init
onMounted(() => {
  loadViews().then(() => {
    loadDashboard();
  });
  loadInvitations();
  loadDashboardCallQueues();
  loadDashboardCalls();
  document.addEventListener('click', closeCallMenu);
  document.addEventListener('click', closeViewSelector);
  document.addEventListener('click', closeDndMenu);
  document.addEventListener('click', () => { colorPickerGroupId.value = null; });

  // Socket.IO real-time listeners for view sharing
  socket.on('dashboardViewUpdated', onDashboardViewUpdated);
  socket.on('dashboardInvitation', onDashboardInvitation);
  socket.on('dashboardShareAccepted', onDashboardShareAccepted);

  // Real-time: refresh "Letzte Anrufe" when a new call record arrives
  socket.on('callRecordCreated', onNewCallRecord);
});

onUnmounted(() => {
  document.removeEventListener('click', closeCallMenu);
  document.removeEventListener('click', closeViewSelector);
  document.removeEventListener('click', closeDndMenu);
  socket.off('dashboardViewUpdated', onDashboardViewUpdated);
  socket.off('dashboardInvitation', onDashboardInvitation);
  socket.off('dashboardShareAccepted', onDashboardShareAccepted);
  socket.off('callRecordCreated', onNewCallRecord);
});
</script>
